/**
 * Klient REST k1p4 (PLAN §7).
 *
 * - sesja jedzie w cookie httpOnly, więc każde żądanie leci z `credentials: same-origin`;
 * - mutacje niosą nagłówek CSRF odczytany z cookie `k1p4_csrf` (double-submit);
 * - błędy API mają jednolity kształt `{ error: { code, message } }` i trafiają
 *   do `ApiError`, żeby ekrany mogły reagować na `code`, a nie na treść komunikatu;
 * - każde wyjście do API przechodzi przez `send()` albo `openFile()` — to one
 *   rozstrzygają, czy bundel rozmawia z serwerem, czy z fiksturami obrazu
 *   demonstracyjnego (`demoTransport()`, ADR-0059).
 */

import type {
  AssistantHint,
  AssistantView,
  DeclarationKind,
  DocumentType,
  DeclarationsResponse,
  KupVariant,
  PayrollComponentKind,
  PayrollPeriodStatus,
  PayrollWarningKind,
  PayrollWarningLevel,
  PayslipStatus,
  PpkStatus,
  SickAbsenceKind,
  SickBaseKind,
  SickLeaveReason,
  TaxCreditDivisor,
  TerminationMode,
  AbsenceBenefitType,
  CareScope,
  RegistrationCorrectionScope,
  TaxReliefKind,
  TaxResidence,
  GarnishmentKind,
  MaternityLeaveKind,
  DevItemStatus,
  DeclarationIssue,
  InsuranceScope,
  LeaveEntitlementKind as SharedLeaveEntitlementKind,
  TimeRegime,
  EmployeeTimeEntryKind,
  WorkHoursExemption,
  HealthState,
  PenaltyVoidBasis,
  InvitationImportParsed,
  MigrationEmployee,
  MigrationFileParsed,
  MigrationHistory,
  MigrationSickLeave,
} from '@k1p4/shared'
import { warsawDay, warsawYear } from '@k1p4/shared'

/* Wyłącznie typ — import znika w budowie, więc nie wciąga fikstur do bundla. */
import type * as DemoTransportModule from './demo/transport'

export const CSRF_COOKIE = 'k1p4_csrf'
export const CSRF_HEADER = 'x-k1p4-csrf'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * Nazwane braki, gdy serwer je wymienił (np. czego brakuje do pliku XML
     * deklaracji). Ekran tłumaczy je na zdania, zamiast pokazywać jeden
     * komunikat „nie udało się".
     */
    readonly issues: readonly string[] = [],
    /** Pozostałe pola błędu z serwera, np. rodzaj i miesiąc kolizji (#67). */
    readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /**
   * Powód odmowy, gdy odpowiedź go niesie — dziś wyłącznie obraz
   * demonstracyjny (`demo-read-only` i pokrewne, ADR-0059). Serwer produkcyjny
   * tego pola nie wysyła, więc w wersji na żywo jest zawsze `null`.
   */
  get reason(): string | null {
    return typeof this.details.reason === 'string' ? this.details.reason : null
  }
}

/** Błąd z koperty API `{ error: { code, message, issues?, … } }` — jedna reguła dla każdej ścieżki. */
function apiErrorFrom(
  status: number,
  payload: unknown,
  fallbackCode: string,
  fallbackMessage: string,
): ApiError {
  const errorBody = payload as {
    error?: { code?: string; message?: string; issues?: string[] } & Record<string, unknown>
  } | null
  const { code, message, issues, ...details } = errorBody?.error ?? {}
  return new ApiError(
    status,
    typeof code === 'string' ? code : fallbackCode,
    typeof message === 'string' ? message : fallbackMessage,
    Array.isArray(issues) ? issues : [],
    details,
  )
}

/**
 * JEDYNY PUNKT ROZGAŁĘZIENIA obrazu demonstracyjnego (ADR-0059, `no-human/infra`
 * ADR 0014). `VITE_DATA_MODE` Vite wpisuje w bundel jako literał W CZASIE
 * BUDOWY — obraz `web-demo` ma tu `'demo'`, każdy inny pusty napis, więc
 * o tym, skąd przychodzą odpowiedzi, decyduje TAG OBRAZU, a nie zmienna
 * wdrożeniowa. Cokolwiek innego niż dokładnie `demo` to wersja na żywo:
 * literówka ma skończyć się widocznym 502 prawdziwego proxy, nie cichym
 * podaniem fikstur jako danych klienta.
 *
 * Moduł fikstur ładujemy DYNAMICZNIE: w bundlu produkcyjnym warunek zwija się
 * do `null` i fikstury nie trafiają do obrazu `web` (ADR-0004 — „fixtury nie
 * istnieją w obrazach"; pilnuje tego każda budowa `deploy/web.Dockerfile`).
 *
 * Czytane przy każdym wywołaniu, nie raz przy imporcie: test przełącza tryb
 * `vi.stubEnv`, a w zbudowanym bundlu to i tak stała.
 */
export function demoTransport(): Promise<typeof DemoTransportModule> | null {
  /*
   * Porównanie MUSI stać wprost w tym wyrażeniu. Wyniesione do funkcji
   * pomocniczej przestaje zwijać się w budowie do stałej, `import()` zostaje
   * w kodzie i Vite emituje chunk z fiksturami także do obrazu `web` —
   * zmierzone; wywraca to sprawdzenie bundla w `deploy/web.Dockerfile`.
   */
  return import.meta.env.VITE_DATA_MODE === 'demo' ? import('./demo/transport') : null
}

/**
 * Wyjście do API: `fetch` pod `/api/v1`, a w obrazie demonstracyjnym odpowiedź
 * z fikstur o tym samym kształcie drutu — ekrany, `ApiError` i obsługa błędów
 * nie wiedzą, z kim rozmawiają.
 */
async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const demo = demoTransport()
  if (demo) return (await demo).demoFetch(path, init)
  return fetch(`/api/v1${path}`, init)
}

/** To samo co `send()`, dla adresów złożonych z prefiksem `/api/v1` (pobrania plików). */
function sendUrl(url: string, init: RequestInit = {}): Promise<Response> {
  return send(url.startsWith('/api/v1') ? url.slice('/api/v1'.length) : url, init)
}

/**
 * Pobranie pliku nawigacją przeglądarki (PDF, CSV, kopia danych). Obraz
 * demonstracyjny plików nie wydaje — odmowa idzie komunikatem, a nie
 * nawigacją pod `/api/v1`, która skończyłaby się stroną błędu proxy.
 */
export function openFile(url: string): void {
  const demo = demoTransport()
  if (demo) {
    void demo.then((module) => module.refuseFile(url))
    return
  }
  globalThis.location.assign(url)
}

/**
 * Adres wyjścia do portalu 33rp z ekranu logowania (`GET /auth/portal`).
 * `next` to ścieżka aplikacji, do której serwer odeśle po powrocie.
 */
export function portalSignInUrl(next: string | null): string {
  return next ? `/api/v1/auth/portal?next=${encodeURIComponent(next)}` : '/api/v1/auth/portal'
}

/**
 * Wejście bez żądania — wyłącznie w obrazie demonstracyjnym, który nie ma
 * portalu, do którego mógłby odesłać (ADR-0059). Zwraca `null` w wersji na
 * żywo: ekran logowania zostaje wtedy przy zwykłym odnośniku do portalu.
 * Funkcja z tej odpowiedzi zapamiętuje sesję demo i mówi, dokąd przejść —
 * tak jak callback portalu wybiera start z `next` albo z roli.
 */
export function localSignIn(): ((next: string | null) => Promise<string>) | null {
  const demo = demoTransport()
  if (!demo) return null
  return async (next) => (await demo).signIn(next)
}

/**
 * „Dziś" ekranów — miesiąc listy płac, tydzień kalendarza, rok PIT-4R.
 *
 * W wersji na żywo to zegar przeglądarki. Obraz demonstracyjny jest stanem
 * firmy z JEDNEGO dnia (dnia zapisu fikstur), więc ekran musi pytać o ten
 * dzień: inaczej po miesiącu każdy widok miesięczny pytałby o okres, którego
 * zapis nie zawiera. Ekrany nie wołają `new Date()` wprost — pilnuje tego test.
 */
export function apiToday(): Date {
  return import.meta.env.VITE_DATA_MODE === 'demo'
    ? new Date(`${DEMO_SNAPSHOT_DATE}T10:00:00Z`)
    : new Date()
}

/**
 * Dzisiejsza doba, miesiąc i rok w POLSCE (fala 7b). `toISOString()` daje dobę
 * UTC, więc między północą a 1.00 (2.00 latem) ekran pokazywał jeszcze wczoraj,
 * a w sylwestra o 23.30 — już nowy rok w UTC dopiero po północy. Ekrany biorą
 * „dziś" stąd, a nie z `apiToday().toISOString()`.
 */
export function apiDay(): string {
  return warsawDay(apiToday())
}

export function apiMonth(): string {
  return apiDay().slice(0, 7)
}

export function apiYear(): number {
  return warsawYear(apiToday())
}

/** Dzień zapisu fikstur obrazu demonstracyjnego (`demo/fixtures.json`, `provenance.capturedOn`). */
export const DEMO_SNAPSHOT_DATE = '2026-10-02'

function readCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length))
  }
  return null
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (method !== 'GET') {
    const csrf = readCookie(CSRF_COOKIE)
    if (csrf) headers[CSRF_HEADER] = csrf
  }

  const response = await send(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (response.status === 204) return undefined as T

  let payload: unknown = null
  const text = await response.text()
  if (text.length > 0) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = text
    }
  }

  if (!response.ok) {
    throw apiErrorFrom(response.status, payload, 'UNKNOWN', `Żądanie nie powiodło się (${response.status})`)
  }

  return payload as T
}

// ---------------------------------------------------------------------------
// Kontrakty odpowiedzi używane przez ekrany
// ---------------------------------------------------------------------------

export type AuthMethod = 'sso' | 'password' | 'magic_link' | 'oauth'

/**
 * Co ekran logowania ma pokazać na TYM hoście (`GET /auth/entry`, ADR-0057).
 * Fakty o wdrożeniu i o hoście, nigdy o koncie.
 */
export interface AuthEntryResponse {
  /** Klient portalu skonfigurowany i host nazywa obszar — jest przycisk. */
  portalSignIn: boolean
  /** Ten host nazywa obszar firmy (a nie apeks). */
  tenantHost: boolean
  /** `k1p4.cloud` (z portem) — do podpowiedzi adresu firmy na apeksie. */
  tenantHostSuffix: string | null
  /** Adres portalu 33rp (skonfigurowany issuer) albo `null`, gdy go nie ma. */
  portalUrl: string | null
  /** `<portal>/register?module=k1p4` — język dokleja ekran. */
  registrationUrl: string | null
}

export interface SessionResponse {
  user: {
    id: string
    email: string
    locale: 'pl' | 'en'
    roles: string[]
    emailVerified: boolean
  }
  tenantId: string
}

export interface TenantMeResponse {
  tenant: {
    id: string
    name: string
    nip: string | null
    country: string | null
    plan: 'start' | 'pro' | 'ent'
    trialEndsAt: string | null
  }
  steps: Array<{ key: string; status: 'todo' | 'done' }>
}

export interface JoinRequestItem {
  id: string
  email: string
  codeOrDomain: string
  status: 'pending' | 'approved' | 'rejected'
  assignedRole: string | null
  createdAt: string
  decidedAt: string | null
}

export interface TenantUserItem {
  id: string
  email: string
  status: string
  emailVerified: boolean
  roles: string[]
}


// --- Kadry (Etap 2) ---------------------------------------------------------

export type EmployeeStatus = 'aktywny' | 'onboarding' | 'terminated' | 'rejected'
export type ContractType =
  | 'uop_nieokreslony'
  | 'uop_okreslony'
  | 'uop_probny'
  | 'zlecenie'
  /** Umowa o dzieło: bez składek, koszty 20 % albo 50 % (Etap 46, ADR-0047). */
  | 'dzielo'
  | 'b2b'

export interface EmployeeListItem {
  id: string
  employeeNo: number
  displayId: string
  firstName: string
  lastName: string
  position: string | null
  department: string | null
  departmentId: string | null
  location: string | null
  employmentType: ContractType | null
  status: EmployeeStatus
  contractEndAt: string | null
  onboardingStage: number
  /** Flagi pochodne [D6] — liczone przez API, nie enum statusu. */
  endingContract: boolean
  missingDocuments: boolean
  manager: string | null
}

export interface EmployeeListResponse {
  items: EmployeeListItem[]
  counts: {
    all: number
    aktywny: number
    onboarding: number
    ending: number
    missingDocs: number
  }
  filters: {
    departments: Array<{ id: string; name: string }>
    locations: string[]
    types: ContractType[]
    managers: Array<{ id: string; label: string }>
  }
  /** Zakres wynikający z roli — UI tylko go odzwierciedla. */
  scope: { teamOnly: boolean; personalData: boolean; compensation: boolean }
}

export interface EmployeeProfileResponse {
  employee: {
    id: string
    displayId: string
    firstName: string
    lastName: string
    position: string | null
    department: string | null
    location: string | null
    employmentType: ContractType | null
    status: EmployeeStatus
    hiredAt: string | null
    contractEndAt: string | null
    fte: string
    manager: string | null
  }
  onboarding: {
    stage: string
    stageNumber: number
    totalStages: number
    sideState: 'question' | 'rejected' | null
    workAdmitted: boolean
    /** Zatrudnienie ustało (Fala 6, grupa 105) — z dniem ustania. */
    employmentEnded?: boolean
    employmentEndedOn?: string | null
    fileComplete: boolean
    /** Etap 8 bez zatwierdzonego orzeczenia (przegląd Fali 6). */
    awaitingMedicalApproval?: boolean
  }
  personalData: {
    pesel: string | null
    birthDate: string | null
    addressStreet: string | null
    addressCityZip: string | null
    citizenship: string | null
    privateEmail: string | null
    phone: string | null
    approvedBy: string | null
    approvedAt: string | null
  } | null
  taxData: {
    taxOffice: string | null
    nfzBranch: string | null
    approvedBy: string | null
    approvedAt: string | null
  } | null
  bankAccount: {
    iban: string | null
    bankName: string | null
    ownerName: string | null
    currency: string
    approvedBy: string | null
    approvedAt: string | null
  } | null
  employmentTerms: {
    position: string | null
    fte: string
    startDate: string | null
    contractType: ContractType | null
    probationMonths: number | null
    baseSalaryGr: number | null
    bonusGr: number | null
    bonusNote: string | null
    workplace: string | null
    workMode: string | null
    departmentId: string | null
    status: string
    /** Kiedy pracownik przyjął warunki i kto je przygotował (grupa 106). */
    acceptedAt: string | null
    preparedBy: string | null
  } | null
  /** Ostatnia przyjęta wersja warunków — baza nowego aneksu (przegląd Fali 6). */
  annexBase: {
    position: string | null
    fte: string
    startDate: string | null
    contractType: ContractType | null
    probationMonths: number | null
    baseSalaryGr: number | null
    bonusGr: number | null
    bonusNote: string | null
    workplace: string | null
    workMode: string | null
    departmentId: string | null
    status: string
    /** Kiedy pracownik przyjął warunki i kto je przygotował (grupa 106). */
    acceptedAt: string | null
    preparedBy: string | null
  } | null
  /** Ustanie zatrudnienia — fakt z trybem i podstawą prawną (Etap 15). */
  termination: {
    id: string
    endDate: string
    mode: TerminationMode
    zusMode: string
    legalBasis: string | null
    initiator: 'pracownik' | 'pracodawca' | null
    reason: string | null
    recordedBy: string | null
    recordedAt: string
  } | null
  /** Aneks czekający na decyzję pracownika — porównanie przed/po (Etap 14). */
  termsProposal: {
    id: string
    startDate: string | null
    changes: Array<{ field: string; before: string | null; after: string | null }>
  } | null
  checklist: Array<{ itemKey: string; done: boolean; note: string | null; labelKey?: string }>
  /** `null` — rola bez „Badania : Podgląd" (dane o zdrowiu, Fala 4 grupa 63). */
  /** Stan każdego rekordu z reguły wspólnej z listą kadr (fala 8, grupa 109). */
  medicalExaminations: Array<{
    examDate: string | null
    validUntil: string | null
    status: string
    state: HealthState
    referralDueOn: string | null
  }> | null
  bhpTrainings: Array<{
    kind: string
    performedAt: string | null
    validUntil: string | null
    conductedBy: string | null
    status: string
    state: HealthState
    referralDueOn: string | null
  }> | null
  scope: {
    personalData: boolean
    compensation: boolean
    /** Badania i BHP w zakresie roli; brak pola = odpowiedź sprzed Fali 4. */
    health?: boolean
    sensitiveRevealed: boolean
  }
}

// --- Proces zatrudnienia (Etap 3) -------------------------------------------

export type OnboardingStage =
  | 'invited'
  | 'email_verified'
  | 'data_submitted'
  | 'data_approved'
  | 'terms_prepared'
  | 'terms_accepted'
  | 'referral_issued'
  | 'medical_approved'
  | 'bhp_confirmed'
  | 'active'

export type OnboardingSideState = 'question' | 'rejected' | null

export interface OnboardingTerms {
  position: string | null
  fte: string
  startDate: string | null
  contractType: ContractType | null
  probationMonths: number | null
  baseSalaryGr: number | null
  bonusNote: string | null
  workplace: string | null
  workMode: string | null
}

export interface OnboardingMessage {
  author: 'employee' | 'hr'
  body: string
  createdAt: string
}

export interface OnboardingSessionResponse {
  organization: { name: string }
  employee: {
    firstName: string
    lastName: string
    position: string | null
    department: string | null
    startDate: string | null
  }
  stage: OnboardingStage
  sideState: OnboardingSideState
  draft: {
    personal: {
      hasPesel: boolean
      birthDate: string | null
      privateEmail: string | null
      phone: string | null
      addressStreet: string | null
      addressCityZip: string | null
    } | null
    tax: {
      /** Kod z wykazu Ministerstwa Finansów — nazwa jest tylko do pokazania. */
      taxOfficeCode: string | null
      taxOffice: string | null
      nfzBranch: string | null
    } | null
    bank: { hasIban: boolean; bankName: string | null } | null
    emergency: { name: string | null; phone: string | null; relation: string | null } | null
  }
  /** `id` — oferta na ekranie; akceptacja ją odsyła (przegląd Fali 5). */
  terms: (OnboardingTerms & { id: string }) | null
  /** Czy ekran warunków pyta o regulamin (Fala 6, zmiana D8). */
  rulesAcknowledgement: 'required' | 'unavailable' | 'not_applicable'
  /** Warunki niosą inny wariant klauzuli RODO niż etap danych — potwierdzenie od nowa (przegląd Fali 6). */
  rodoRenewal?: boolean
  /** Skierowanie w obiegu — ekran badań pilnuje z niego kolejności dat (Fala 6, grupa 120). */
  medical: { referralIssuedAt: string | null; referralDeadlineDays: number | null } | null
  messages: OnboardingMessage[]
}

export interface InviteBody {
  email: string
  firstName: string
  lastName: string
  departmentName?: string
  position?: string
  startDate?: string
  employmentType?: ContractType
  managerName?: string
}

export interface CreatedInvitation {
  id: string
  /** Kod w plaintekście przychodzi JEDEN raz — potem w bazie jest tylko hash. */
  code: string
  joinUrl: string
  expiresAt: string
  /** Akta osoby w trakcie procesu, do których wraca ten kod (Fala 6, grupa 30). */
  employeeId: string | null
}

export interface InvitationListItem {
  id: string
  codePrefix: string
  email: string
  firstName: string
  lastName: string
  position: string | null
  status: 'pending' | 'used' | 'expired' | 'revoked'
  expiresAt: string
  employeeId: string | null
}

export interface EmployeeOnboardingResponse {
  stage: OnboardingStage
  stageNumber: number
  sideState: OnboardingSideState
  /** Akcje realnie dostępne dla działu HR w tym stanie — liczy je maszyna stanów. */
  actions: string[]
  /** Etap 8 bez zatwierdzonego orzeczenia — oś pokazuje zatwierdzenie jako bieżące (przegląd Fali 6). */
  awaitingMedicalApproval?: boolean
  invitation: { email: string; status: string; expiresAt: string } | null
  events: Array<{
    fromStage: number | null
    toStage: number
    actorLabel: string | null
    createdAt: string
    meta: Record<string, unknown> | null
  }>
  messages: OnboardingMessage[]
  terms: (OnboardingTerms & { status: string; departmentId: string | null }) | null
  /** Plan zatrudnienia z warunków, akt i zaproszenia — start formularza warunków. */
  defaults: {
    position: string | null
    departmentId: string | null
    startDate: string | null
    contractType: ContractType | null
  } | null
  medical: {
    referralIssuedAt: string | null
    referralDeadlineDays: number | null
    examDate: string | null
    validUntil: string | null
    status: string
  } | null
}

export interface PrepareTermsBody {
  position: string
  departmentId?: string | null
  /** Tylko przy umowie o pracę — poza nią etatu nie ma. */
  fte?: number
  startDate: string
  contractType: ContractType
  probationMonths?: number | null
  baseSalaryGr: number
  bonusNote?: string | null
  workplace?: string | null
  workMode?: string | null
}

export interface InvitationPreview {
  organizationName: string
  firstName: string
  lastName: string
  position: string | null
  departmentName: string | null
  startDate: string | null
}

export interface DepartmentRow {
  id: string
  name: string
  /** Ile osób jest przypisanych — dział z ludźmi nie znika jednym kliknięciem. */
  employees: number
}

export type OnboardingDocumentKey = 'rodo' | 'info' | 'regulamin'

export interface OnboardingDocument {
  key: OnboardingDocumentKey
  title: string
  version: string
  status: 'ready' | 'unavailable'
  note: string | null
  sections: Array<{ heading: string | null; paragraphs: string[] }>
  contentHash: string
}

// --- Wnioski (Etap 4) -------------------------------------------------------

export type ChangeRequestField =
  | 'address'
  | 'phone'
  | 'private_email'
  | 'bank_account'
  | 'tax_office'
  | 'nfz_branch'

export type RequestStatus = 'pending' | 'approved' | 'rejected'

export interface ChangeRequestItem {
  id: string
  employeeId: string
  fieldKey: ChangeRequestField
  /**
   * Wartości prezentacyjne — dla pól wrażliwych zamaskowane po stronie API,
   * a poza zakresem roli puste (`valuesHidden`, #19).
   */
  beforeValue: string | null
  afterValue: string | null
  comment: string | null
  status: RequestStatus
  createdAt: string
  createdByLabel: string
  decidedAt: string | null
  decisionComment: string | null
  attachmentFileId: string | null
  attachmentName: string | null
  approverEmail: string | null
  employeeNo: number
  firstName: string
  lastName: string
  /** Pole spoza zakresu roli: bez wartości, komentarzy i załącznika (#19). */
  valuesHidden: boolean
}

export type PortalRequestType =
  | 'urlop_wypoczynkowy'
  | 'urlop_na_zadanie'
  | 'praca_zdalna'
  | 'delegacja'
  | 'zwolnienie_lekarskie'
  | 'zaswiadczenie_o_zatrudnieniu'
  | 'zaswiadczenie_o_zarobkach'
  | 'zmiana_adresu'
  | 'zmiana_danych_osobowych'
  | 'zmiana_rachunku'
  /** Zgłoszenie albo wyrejestrowanie członka rodziny — ZUS ZCNA (Etap 37). */
  | 'czlonek_rodziny'

export interface PortalRequestTypeItem {
  type: PortalRequestType
  /** `leave` otwiera kreator urlopu z saldem (fala 8, grupa 40). */
  kind: 'request' | 'change' | 'family' | 'leave'
  field: ChangeRequestField | null
  /** Pola zmieniane kafelkiem — przy kilku portal pyta o wybór (grupa 38). */
  fields?: ChangeRequestField[]
  /** Typ urlopu ze słownika dla `kind: 'leave'`. */
  leaveTypeKey?: string | null
  needsDateRange: boolean
}

/** Kafelek w konfiguracji firmy (fala 8, grupa 37; D97). */
export interface RequestTypeSetting extends PortalRequestTypeItem {
  enabled: boolean
  /** Ustawowy albo zawsze włączony — firma go nie wyłączy. */
  locked: 'statutory' | 'always_on' | null
}

/** Wniosek o zmianę danych w historii portalu — wartości zamaskowane przy zapisie. */
export interface PortalChangeRequestItem {
  id: string
  fieldKey: ChangeRequestField
  beforeValue: string | null
  afterValue: string
  status: RequestStatus
  createdAt: string
  decidedAt: string | null
  decisionComment: string | null
  /** Fałsz, gdy wniosek do akt złożyły kadry. */
  submittedByMe: boolean
}

/** Wniosek urlopowy w historii portalu (grupa 40). */
export interface PortalLeaveHistoryItem {
  id: string
  typeKey: string
  labelPl: string
  labelEn: string
  dateFrom: string
  dateTo: string
  days: number
  status: string
  createdAt: string
  decidedAt: string | null
  decisionComment: string | null
}

/**
 * Dane wniosku o członka rodziny widziane przez ekran — numer PESEL wyłącznie
 * jako maska, bo to dane osoby trzeciej (Etap 37).
 */
export interface PortalRequestFamily {
  action: 'zgloszenie' | 'wyrejestrowanie'
  memberId: string | null
  memberLabel: string | null
  kinshipCode: string | null
  kinshipName: string | null
  pesel: string | null
  birthDate: string | null
  entitlementFrom: string | null
  entitlementTo: string | null
  disabilityCode: string | null
  sharedHousehold: boolean | null
}

export interface PortalRequestItem {
  id: string
  type: PortalRequestType
  dateFrom: string | null
  dateTo: string | null
  note: string | null
  status: RequestStatus
  createdAt: string
  decidedAt: string | null
  decisionComment: string | null
  /** Wypełnione tylko dla wniosku o członka rodziny. */
  family?: PortalRequestFamily | null
}

export interface HrRequestItem extends PortalRequestItem {
  employeeId: string
  employeeNo: number
  firstName: string
  lastName: string
  position: string | null
}

export interface HrRequestsResponse {
  items: HrRequestItem[]
  counts: { all: number; pending: number; approved: number; rejected: number }
}

export interface UploadedFile {
  id: string
  filename: string
  mime: string
  size: number
}

// --- Portal pracownika (Etap 5) ---------------------------------------------

/**
 * Okres zatrudnienia w portalu (Fala 7, grupa 44): `endDate` to ostatni dzień
 * zatrudnienia (`null` bez ustania albo przy ustaniu bez daty), `ended` — czy
 * ustało przed dziś. W okresie wypowiedzenia `endDate` jest, a `ended` nie.
 */
export interface PortalEmployment {
  endDate: string | null
  ended: boolean
}

export interface PortalDashboardResponse {
  hasEmployeeRecord: boolean
  employee?: { firstName: string; lastName: string; position: string | null }
  year?: number
  /** Umowa cywilna: 'nie_dotyczy' i `leave` = null (art. 152 § 1 k.p., D102). */
  leaveEntitlement?: LeaveEntitlementKind
  leave?: LeaveBalanceItem | null
  /** Kto rozstrzyga wniosek urlopowy: przełożony z akt albo — bez niego — kadry. */
  leaveApprover?: 'manager' | 'hr'
  employment?: PortalEmployment
  /** Po ustaniu: ekwiwalent za urlop na zatwierdzonej liście (art. 171 § 1 k.p.). */
  leaveEquivalentPaid?: boolean
  documentsToSign?: number
  pendingRequests?: number
  pendingLeaves?: number
  /** Moduł płac to faza 2 — dopóki go nie ma, wartość jest pusta [D20]. */
  /** Ostatnie naliczenie: kwota netto i data wypłaty; `null` = brak listy płac. */
  /**
   * Kafel wypłaty (fala 8, grupa 20; D108): kwota do wypłaty po potrąceniach,
   * najbliższa data nie wcześniejsza niż dziś albo — bez niej — ostatnia.
   */
  nextPayout?: {
    payoutGr: number
    netGr: number
    date: string | null
    month: string
    upcoming: boolean
    draft: boolean
  } | null
}

export interface PortalDocumentItem {
  id: string
  type: DocumentType
  title: string
  status: 'szkic' | 'do_akceptacji' | 'do_podpisu' | 'podpisany' | 'zarchiwizowany'
  date: string | null
  signedAt: string | null
  fileId: string | null
  fileName: string | null
  /** Zapisana treść dokumentu — PDF powstaje przy pobraniu, jeśli pliku jeszcze nie ma (grupa 48). */
  hasContent: boolean
  /** Chwila zapoznania — tylko przy odpisach akceptacji z procesu zatrudnienia (grupa 134). */
  acknowledgedAt: string | null
}

/** Rodzaje wpisu formularza portalu — z jednej stałej (fala 7b, #39). */
export type TimeEntryKind = EmployeeTimeEntryKind

export interface PortalTimeEntry {
  id: string
  date: string
  hours: string
  /* Na liście bywa też wpis kadr, np. nieobecność nieusprawiedliwiona. */
  kind: TimeEntryKind | 'nieusprawiedliwiona'
  note: string | null
  approved: boolean
  approvedAt: string | null
  /** Godzina rozpoczęcia i zakończenia (HH:MM:SS); `null` = nie zapisano (fala 7b, grupa 131). */
  startsAt: string | null
  endsAt: string | null
}

/** Wpis czasu pracy w widoku akceptującego (spec 01 §4). */
export interface TimeApprovalEntry extends PortalTimeEntry {
  employeeId: string
  firstName: string
  lastName: string
}

export interface MigrationBody {
  cutoverMonth: string
  employees: string
  history?: string
  sickLeaves?: string
}

export interface MigrationAnalysis {
  cutoverMonth: string
  employees: MigrationFileParsed<MigrationEmployee>
  history: MigrationFileParsed<MigrationHistory> | null
  sickLeaves: MigrationFileParsed<MigrationSickLeave> | null
  blockers: Array<{ code: 'closedPayroll' | 'cutoverMismatch' | 'keyringMissing'; cutoverMonth?: string }>
  summary: { people: number; historyMonths: number; sickLeaves: number; warnings: number }
  valid: boolean
}

export interface MigrationResult {
  people: number
  historyPeriods: number
  sickLeaves: number
  mailed: number
  notMailed: Array<{ line: number; email: string }>
  signInUrl: string
}

export interface MigrationState {
  cutoverMonth: string | null
  migratedPeople: number
  locked: boolean
}

/** Podgląd importu zaproszeń: wiersze z błędami przy polu i błędy całego pliku. */
export type InvitationImportPreview = InvitationImportParsed & { valid: boolean }

export interface InvitationImportResult {
  created: number
  mailed: number
  /** Wiersze, do których mail nie wyszedł — link do przekazania inną drogą. */
  notMailed: Array<{ line: number; email: string; joinUrl: string }>
  expiresAt: string
}

export interface PortalTimeResponse {
  hasEmployeeRecord: boolean
  month?: string
  /**
   * Jak ewidencja dotyczy osoby (fala 7b, #35; D102): pracownik, same godziny
   * zlecenia albo „nie dotyczy" przy dziele i B2B. Poza `kodeks_pracy` wymiar,
   * nadgodziny i pora nocna przychodzą jako `null`.
   */
  regime?: TimeRegime
  employment?: PortalEmployment
  /** Kto zatwierdza wpisy: przełożony z akt albo — bez niego — kadry. */
  approver?: 'manager' | 'hr'
  /** Wymiar okresu z art. 130 dla pełnego etatu. */
  normHours?: number | null
  /** Wymiar pracownika z etatu (art. 29 § 1 pkt 4 k.p., fala 7b, #25). */
  employeeNormHours?: number | null
  employeeNormHoursAfterAbsence?: number | null
  /** Etat w punktach bazowych (10 000 = pełny). */
  fteBp?: number | null
  workedHours?: number
  /*
   * Nadgodziny liczone z rozkładu, a pora nocna z okna firmy (Etap 42) — nie
   * z rodzaju wpisu. Makieta portalu ma trzy kafelki i tyle ich zostaje;
   * godziny nocne pracownik widzi na pasku jako dodatek nocny.
   */
  overtimeHours?: number | null
  nightHours?: number | null
  remoteDays?: number
  periodStatus?: 'open' | 'review' | 'closed'
  entries: PortalTimeEntry[]
}

export interface LeaveBalanceItem {
  typeKey: string
  labelPl: string
  labelEn: string
  /** Dni urlopu; przy puli w godzinach wyprowadzone z godzin (h / 8), więc bywają ułamkiem. */
  limitDays: number | null
  usedDays: number
  remainingDays: number | null
  subpoolOf: string | null
  /** Pula w godzinach (art. 154(2) § 2) — tylko wypoczynkowy; `null` przy puli w dniach. */
  limitHours?: number | null
  usedHours?: number | null
  remainingHours?: number | null
  /** Miesiące proporcji roku ustania (art. 155(1) k.p.); `null` = pełny wymiar. */
  proportionalMonths?: number | null
  /** Wymiar podstawowy ze słownika, nie ze stażu — do potwierdzenia w aktach. */
  baseAssumed?: boolean
  fteAssumed?: boolean
}

/** Stan prawa do urlopu według rodzaju umowy (D102). */
export type LeaveEntitlementKind = SharedLeaveEntitlementKind

export interface LeaveItem {
  id: string
  typeKey: string
  dateFrom: string
  dateTo: string
  days: number
  status: 'pending' | 'approved' | 'rejected' | 'change_requested'
  note: string | null
  decisionComment: string | null
  createdAt: string
}

export interface PortalLeavesResponse {
  hasEmployeeRecord: boolean
  year?: number
  leaveEntitlement?: LeaveEntitlementKind
  balances: LeaveBalanceItem[]
  /** Godziny dnia pracy przy dzisiejszym etacie (8 × etat); `null` bez puli. */
  workDayHours?: number | null
  employment?: PortalEmployment
  absences: LeaveItem[]
  /** Zwolnienia lekarskie z rejestru kadr — osobna lista, nie wnioski. */
  sickLeaves: SickLeaveItem[]
  types?: Array<{ key: string; defaultDays: number | null }>
}

/** Stan badań i BHP z `@k1p4/shared/health` (fala 8, grupy 26–28). */
export type ValidityStatus = HealthState

export interface PortalHealthResponse {
  hasEmployeeRecord: boolean
  summary?: { medical: HealthState; bhp: HealthState }
  medical: Array<{
    id: string
    examDate: string | null
    validUntil: string | null
    status: string
    validity: HealthState
    referralDueOn?: string | null
  }>
  bhp: Array<{
    id: string
    kind: string
    status?: string
    performedAt: string | null
    validUntil: string | null
    conductedBy: string | null
    validity: HealthState
    referralDueOn?: string | null
  }>
}

export interface PortalTaxResponse {
  items: Array<{
    id: string
    type: string
    title: string
    date: string | null
    fileId: string | null
    /** Zapisana treść — informacja roczna bez pliku pobiera się z renderem na żądanie (grupa 48). */
    hasContent: boolean
  }>
  /** Brak integracji PPK — nie zmyślamy salda oszczędności. */
  ppk: null
  /**
   * Wpłaty PPK policzone przez nasz silnik (to nasze własne liczby, w odróżnieniu
   * od salda, które prowadzi instytucja finansowa). `null` = brak naliczeń.
   */
  ppkContributions: { year: number; employeeGr: number; employerGr: number } | null
  /** Stan uczestnictwa w PPK z akt; `null` = brak deklaracji płacowej (grupa 169). */
  ppkStatus?: PpkStatus | null
  /** Rok, za który pokazujemy wpłaty — rok miesiąca wypłaty w Polsce. */
  /** Czy PPK obejmuje osobę (umowa obowiązująca dziś); `null` = nie wiadomo (grupa 169). */
  ppkCovered: boolean | null
  ppkYear?: number
}

// --- Listy płac (Etap 9, P5) -------------------------------------------------

export interface PayrollPeriodItem {
  id: string
  /** Miesiąc PRZEPRACOWANY w formacie rrrr-mm. */
  month: string
  /** Miesiąc WYPŁATY = miesiąc rozliczeniowy deklaracji i limitów (ADR-0013). */
  settlementMonth: string
  /** Realna data wypłaty zapisana przy okresie. */
  payoutDate: string
  label: string | null
  status: PayrollPeriodStatus
  peopleCount: number
  /** `null` dopóki okres nie został przeliczony — wtedy lista nie pokazuje kwoty. */
  employerCostGr: number | null
  warningCount: number
  calculatedAt: string | null
}

export interface PayrollPeriodsResponse {
  engineAvailable: boolean
  /** Lata, dla których są parametry naliczeń — z nich wybiera się miesiąc. */
  parameterYears: number[]
  items: PayrollPeriodItem[]
}

export interface PayrollWarningItem {
  id: string
  kind: PayrollWarningKind
  level: PayrollWarningLevel
  employeeId: string | null
  employeeName: string | null
  params: Record<string, string | number>
}

export interface PayrollComponentTotalItem {
  kind: PayrollComponentKind
  amountGr: number
  peopleCount: number
  units: number | null
}

export interface PayslipRow {
  id: string
  employeeId: string
  employeeNo: number
  employeeName: string
  contractType: string | null
  grossGr: number
  netGr: number
  employerCostGr: number
  status: PayslipStatus
}

export interface PayrollExtraItem {
  id: string
  employeeId: string
  employeeName: string
  kind: PayrollComponentKind
  /** Kwota w groszach; potrącenie jest ujemne. */
  amountGr: number
  note: string | null
  createdBy: string | null
  createdAt: string
}

export interface PayrollExtraInput {
  employeeId: string
  kind: PayrollComponentKind
  /** Kwota dodatnia — znak dokłada serwer wg rodzaju składnika. */
  amountGr: number
  note?: string
}

/** Brak pola = bez zmian, `null` = usuń (Fala 6, grupa 23). */
export interface TermsChangeInput {
  position: string
  departmentId?: string | null
  fte: number
  startDate: string
  contractType: ContractType
  baseSalaryGr: number
  bonusGr?: number | null
  bonusNote?: string | null
  workplace?: string | null
  workMode?: string | null
  reason: string
}

export interface PayrollPeriodDetailResponse {
  id: string
  /** Miesiąc PRZEPRACOWANY (rrrr-mm). */
  month: string
  /** Miesiąc WYPŁATY = miesiąc rozliczeniowy podatku i składek (ADR-0013). */
  settlementMonth: string
  payoutDate: string
  label: string | null
  status: PayrollPeriodStatus
  peopleCount: number
  grossGr: number
  netGr: number
  socialEmployerGr: number
  ppkEmployerGr: number
  employerCostGr: number | null
  previousEmployerCostGr: number | null
  /** Podział osób listy według umowy; dzieło osobno, bo nie jest stosunkiem pracy (grupa 61). */
  employmentSplit: { uop: number; zlecenie: number; dzielo?: number; b2b: number }
  calculatedAt: string | null
  /** Jednorazowe składniki wpisane do tego okresu (Etap 14). */
  extras: PayrollExtraItem[]
  /** Ile razy okres wrócił z zamkniętej listy do weryfikacji (Etap 13). */
  reopenCount: number
  reopenReason: string | null
  reopenedAt: string | null
  parametersYear: number | null
  parametersConfirmed: boolean
  warningCount: number
  componentTotals: PayrollComponentTotalItem[]
  warnings: PayrollWarningItem[]
  payslips: PayslipRow[]
}

export interface PayslipComponentItem {
  kind: PayrollComponentKind
  amountGr: number
  units: number | null
  note: string | null
}

export interface PayslipDetailResponse {
  id: string
  employeeId: string
  employeeNo: number
  employeeName: string
  month: string
  periodLabel: string | null
  status: PayslipStatus
  contractType: string | null
  components: PayslipComponentItem[]
  grossGr: number
  netGr: number
  pensionEmployeeGr: number
  disabilityEmployeeGr: number
  sicknessEmployeeGr: number
  socialEmployeeGr: number
  healthGr: number
  healthCapApplied: boolean
  kupGr: number
  taxBaseGr: number
  taxCreditGr: number
  taxAdvanceGr: number
  /** Podatek zryczałtowany (art. 30 ust. 1 pkt 5a PIT) — Etap 24. */
  flatTaxGr?: number
  /** Podatek u źródła nierezydenta (art. 29 ust. 1 pkt 1 PIT) — Etap 48. */
  withholdingTaxGr?: number
  /** Zastosowana stawka; `null` = pasek rezydenta (Etap 48). */
  withholdingTaxBp?: number | null
  youthReliefGr: number
  ppkEmployeeGr: number
  ppkEmployerGr: number
  pensionEmployerGr: number
  disabilityEmployerGr: number
  accidentGr: number
  laborFundGr: number
  guaranteedBenefitsGr: number
  socialEmployerGr: number
  employerCostGr: number
  payoutDate: string | null
  /** Wynagrodzenie chorobowe ze środków pracodawcy (art. 92 k.p.). */
  sickPayGr: number
  /** Zasiłki wypłacone przez płatnika: chorobowy i macierzyński; 0 gdy wypłaca je ZUS. */
  benefitGr: number
  /** Część zasiłku będąca macierzyńskim — objęta ulgą dla młodych (Etap 29). */
  maternityBenefitGr: number
  sickDays: number
  benefitDays: number
  absences: SickAbsenceItem[]
}

/** Okres niezdolności na pasku — z kodem, który idzie do raportu ZUS RSA. */
export interface SickAbsenceItem {
  kind: SickAbsenceKind
  zusCode: string
  dateFrom: string
  dateTo: string
  days: number
  amountGr: number
  paidByPayer: boolean
}

/** Rejestr zwolnień lekarskich (Etap 11). */
export interface SickLeaveItem {
  id: string
  employeeId: string
  employeeName: string
  employeeNo: number | null
  dateFrom: string
  dateTo: string
  days: number
  reason: SickLeaveReason
  /** Rodzaj świadczenia (Etap 21): chorobowe, opiekuńcze albo macierzyńskie. */
  benefitType: AbsenceBenefitType
  careScope: CareScope | null
  maternityKind: MaternityLeaveKind | null
  maternityCombined: boolean
  ezlaNumber: string | null
  note: string | null
  baseGr: number | null
  baseKind: SickBaseKind | null
  /** Miesiąc z domkniętą listą płac — wpisu nie da się już usunąć. */
  locked: boolean
  /** Miesiące, których domknięta lista płac nie zawiera tego zwolnienia. */
  correctionMonths: string[]
  registeredAt: string
}

/** Stan puli świadczeń jednej osoby (Etap 21) — liczony z faktów, nie wpisany. */
export interface BenefitPoolItem {
  employeeId: string
  employeeName: string
  benefitPeriodUsed: number
  benefitPeriodLimit: number
  benefitPeriodEndsOn: string | null
  benefitPeriodStartedOn: string | null
  careUsed: Record<CareScope, number>
  year: number
}

export interface SickLeavesResponse {
  items: SickLeaveItem[]
  /** Okres zasiłkowy i limit opiekuńczego dla osób z rejestru (Etap 21). */
  pools: BenefitPoolItem[]
  benefitPayer: boolean
  canRegister: boolean
}

export interface SickLeaveInput {
  employeeId: string
  dateFrom: string
  dateTo: string
  reason: SickLeaveReason
  ezlaNumber?: string
  note?: string
  /** Rodzaj świadczenia (Etap 21): od niego zależy pula i wymagane pola. */
  benefitType?: AbsenceBenefitType
  careScope?: CareScope
  maternityKind?: MaternityLeaveKind
  maternityCombined?: boolean
  /** Zwolnienie na dni z wpisami pracy — świadomie, z audytem (fala 7b, grupa 133). */
  acknowledgeWorkEntries?: true
}

export interface PayrollParametersResponse {
  year: number
  availableYears: number[]
  parameters: {
    year: number
    pensionEmployeeBp: number
    pensionEmployerBp: number
    disabilityEmployeeBp: number
    disabilityEmployerBp: number
    sicknessEmployeeBp: number
    laborFundBp: number
    guaranteedBenefitsBp: number
    healthBp: number
    taxFirstRateBp: number
    taxSecondRateBp: number
    taxThresholdGr: number
    taxCreditAnnualGr: number
    kupBasicGr: number
    kupRaisedGr: number
    mandateKupBp: number
    youthReliefLimitGr: number
    ppkEmployeeDefaultBp: number
    ppkEmployerDefaultBp: number
    /** `null` = kwota ogłaszana na dany rok nie została wpisana. */
    annualSocialCapGr: number | null
    minimumWageGr: number | null
    /**
     * Kwoty wolne od potrąceń z zasiłków (art. 141 ustawy emerytalnej) —
     * ogłaszane komunikatem Prezesa ZUS, waloryzowane od 1 marca (Etap 36).
     */
    benefitFreeAlimonyGr: number | null
    benefitFreeOtherGr: number | null
    nightAllowanceBp: number
    overtimeDayBp: number
    overtimeNightBp: number
    /** Absencja chorobowa (Etap 11): stawki, pule dni i okres wyczekiwania. */
    sickPayRateBp: number
    sickPayFullRateBp: number
    sickPayDaysLimit: number
    sickPaySeniorDaysLimit: number
    sickPaySeniorAge: number
    sickDailyDivisor: number
    sickBaseMonths: number
    sickWaitingDays: number
    benefitPeriodDays: number
    benefitPayerFeeBp: number
    /**
     * Dni terminów z art. 47 ust. 1 u.s.u.s.: pkt 4 (pozostali), pkt 2
     * (jednostki budżetowe) i pkt 3 (osobowość prawna) — #10.
     */
    zusDeadlineDay: number
    zusDeadlineDayBudget: number
    zusDeadlineDayLegalPerson: number
    pitDeadlineDay: number
    ppkDeadlineDay: number
    legalBasis: string
  }
  /** Dane identyfikacyjne płatnika — potrzebne w deklaracjach i pliku KEDU. */
  payer: {
    nip: string | null
    regon: string | null
    /** Rachunki do pliku przelewów: firmy, ZUS (e-składka) i podatkowy (Etap 19). */
    bankAccount: string | null
    zusAccount: string | null
    taxAccount: string | null
    /** Rachunek instytucji finansowej PPK (art. 28 ustawy o PPK, #85). */
    ppkAccount: string | null
    /** Kod urzędu skarbowego płatnika do deklaracji rocznych (Etap 28). */
    taxOfficeCode: string | null
  }
  settings: {
    accidentBp: number | null
    payoutDay: number
    payoutNextMonth: boolean
    deviationAlertBp: number
    /** Kod terminu przysyłania deklaracji ze zgłoszenia płatnika. */
    zusTermCode: string | null
    /** Dzień terminu składek z kodu terminu; `null` = kodu nie ma albo jest spoza mapy. */
    zusDeadlineDay: number | null
    /** Okno pory nocnej z art. 151(7) § 1 k.p. w postaci HH:MM (Etap 42). */
    nightFrom: string
    nightTo: string
    /** Czy firma sama wypłaca zasiłki (art. 61 ustawy zasiłkowej). */
    benefitPayer: boolean
    benefitPayerFee: boolean
    confirmed: boolean
  }
}

/** Dzień zestawienia wykonania z rozkładem (Etap 42). */
export interface TimeReviewDay {
  date: string
  plannedHours: number
  workedHours: number
  nightHours: number
  overtimeHours: number
  /** 5000 albo 10 000 punktów bazowych; `null` = brak nadgodzin. */
  overtimeRateBp: number | null
  plannedOff: boolean
  fromDeclaration: boolean
  clockMissing: boolean
}

/** Wpis rejestru czasu wolnego w zamian za nadgodziny (art. 151(2) k.p., Etap 43). */
export interface OvertimeTimeOffItem {
  id: string
  mode: 'wniosek' | 'pracodawca'
  grantedFrom: string
  grantedTo: string
  grantedHours: number
  /** Ile godzin nadliczbowych pokrywa — 1:1 albo 1,5:1, wprost z przepisu. */
  coveredHours: number
  /** Czy zmieścił się w okresie rozliczeniowym (wymóg § 2). */
  inPeriod: boolean
  note: string | null
  createdAt: string
}

export interface OvertimeTimeOffResponse {
  month: string
  /** Nadgodziny, których dodatek można jeszcze oddać czasem wolnym. */
  remainingOvertimeHours: number
  coveredHours: number
  lateHours: number
  /** Status listy płac miesiąca nadgodzin; `null` = listy jeszcze nie ma. */
  payrollStatus: string | null
  /** Stan okresu ewidencji — wpis po zamknięciu ekran nazywa (fala 7b, grupa 136). */
  timePeriodStatus: 'open' | 'review' | 'closed'
  items: OvertimeTimeOffItem[]
}

/** Dyżur w rejestrze po rekompensacie (art. 151(5) k.p., Etap 50). */
export interface DutyShiftItem {
  id: string
  date: string
  place: 'zaklad' | 'dom'
  hours: number
  /** Godziny pracy wykonanej w czasie dyżuru — wchodzą do czasu pracy (§ 2). */
  workedHours: number
  timeOffHours: number
  /** Sama gotowość: dyżur minus praca wykonana w jego czasie. */
  standbyHours: number
  /** Godziny do zapłaty stawką osobistego zaszeregowania (§ 3). */
  paidHours: number
  /** Dlaczego rekompensata nie przysługuje; `null` = przysługuje. */
  excluded: 'dom' | 'zarzadzajacy' | null
  /** Ile godzin odpoczynku dobowego zabrał ten dyżur (§ 2). */
  restShortageHours: number
  /** Praca z dyżuru bez wpisu w ewidencji (fala 7b, grupa 50). */
  workNotRecordedHours: number
  note: string | null
}

export interface DutyShiftsResponse {
  month: string
  standbyHours: number
  timeOffHours: number
  paidHours: number
  restShortageHours: number
  workNotRecordedHours: number
  payrollStatus: string | null
  /** Stan okresu ewidencji — wpis po zamknięciu ekran nazywa (fala 7b, grupa 136). */
  timePeriodStatus: 'open' | 'review' | 'closed'
  items: DutyShiftItem[]
}

/** Dzień pracy w niedzielę albo święto z dniem wolnym w zamian (Etap 49). */
export interface SubstituteDayView {
  date: string
  reason: 'niedziela' | 'swieto' | 'dzien_wolny_rozkladu'
  hours: number
  /** Dzień wolny oddany za ten dzień; `null` = jeszcze nie udzielono. */
  grantedOn: string | null
  /** Czy zmieścił się w terminie z art. 151(11) § 1. */
  inTerm: boolean
}

export interface SubstituteDaysOffItem extends SubstituteDayView {
  id: string
  workDate: string
  note: string | null
  createdAt: string
  /** Okno terminu z przepisu — sześć dni albo okres rozliczeniowy. */
  window: { from: string; to: string }
}

export interface SubstituteDaysOffResponse {
  month: string
  period: SettlementPeriodView
  /** Niedziele i święta z obowiązkiem z art. 151(11) § 1. */
  sundayDays: SubstituteDayView[]
  /** Praca w dniu wolnym z rozkładu (art. 151(3)) — dokumentacja, nie pasek. */
  freeDayWork: SubstituteDayView[]
  /** Godziny bez dnia wolnego w terminie — z nich liczy się dodatek 100 %. */
  uncompensatedHours: number
  sundayStreakDate: string | null
  payrollStatus: string | null
  /** Stan okresu ewidencji — wpis po zamknięciu ekran nazywa (fala 7b, grupa 136). */
  timePeriodStatus: 'open' | 'review' | 'closed'
  items: SubstituteDaysOffItem[]
}

export interface TimeReviewResponse {
  month: string
  normHours: number
  /** Stosunek pracy — bez niego zestawienie ma same godziny (fala 7b, #35). */
  laborCode: boolean
  fteBp: number
  /** Wymiar pracownika z etatu (fala 7b, #25). */
  employeeNormHours: number
  employeeNormHoursAfterAbsence: number
  window: { startsAt: string; endsAt: string }
  scheduleMissing: boolean
  schedulePublishedOn: string | null
  plannedHours: number
  workedHours: number
  nightHours: number
  overtimeDailyHours: number
  overtimeDayRateHours: number
  overtimeNightRateHours: number
  overtimeWeeklyHours: number
  clockMissingDays: number
  /** System czasu pracy i okres rozliczeniowy (Etap 44, ADR-0045). */
  system: WorkTimeSystem
  hoursTracked: boolean
  /** Powód z art. 149 § 2 k.p.; `null` = godziny się ewidencjonuje (fala 7b, grupa 81). */
  hoursNotTrackedReason: WorkHoursExemption | null
  period: SettlementPeriodView
  /** Nadgodziny średniotygodniowe całego okresu — liczone w jego ostatnim miesiącu. */
  periodOvertimeHours: number
  /** Wymiar okresu po obniżeniu o usprawiedliwioną nieobecność (art. 130 § 3). */
  normHoursAfterAbsence: number
  /** Czas wolny w zamian za nadgodziny (art. 151(2) k.p., Etap 43). */
  timeOffCoveredHours: number
  timeOffGrantedHours: number
  timeOffLateHours: number
  /** Granice pracy nadliczbowej; `null`, gdy o nie nie pytano (Etap 45). */
  limits: OvertimeLimitsView | null
  /** Dzień wolny w zamian za niedzielę i święto (art. 151(11), Etap 49). */
  sundayDays: SubstituteDayView[]
  sundayUncompensatedHours: number
  sundayStreakDate: string | null
  /** Dyżury miesiąca po rekompensacie (art. 151(5) k.p., Etap 50). */
  duty: {
    standbyHours: number
    timeOffHours: number
    paidHours: number
    restShortageHours: number
    workNotRecordedHours: number
    shifts: DutyShiftItem[]
  }
  /** Doby z odpoczynkiem krótszym niż 11 h z ewidencji (art. 132 § 1, fala 7b, grupa 175). */
  restBreaches: Array<{ date: string; restHours: number }>
  /** Ryczałty za nadgodziny i za pracę w nocy (Etap 51). */
  lumpSum: LumpSumView
  days: TimeReviewDay[]
}

export interface PayrollSettingsInput {
  accidentBp?: number | null
  zusTermCode?: string | null
  payoutDay?: number
  payoutNextMonth?: boolean
  deviationAlertBp?: number
  annualSocialCapGr?: number | null
  minimumWageGr?: number | null
  /** Kwoty wolne od potrąceń z zasiłków — art. 141 ustawy emerytalnej (Etap 36). */
  benefitFreeAlimonyGr?: number | null
  benefitFreeOtherGr?: number | null
  /** Okno pory nocnej — osiem godzin między 21:00 a 7:00 (Etap 42). */
  nightFrom?: string
  nightTo?: string
  benefitPayer?: boolean
  benefitPayerFee?: boolean
  confirmed?: boolean
}

export interface PayrollProfileItem {
  employeeId: string
  employeeNo: number
  employeeName: string
  contractType: string | null
  /** Czy w aktach odhaczono PIT-2 — podstawa domyślnej kwoty zmniejszającej. */
  pit2Filed: boolean
  declared: boolean
  ppkStatus: PpkStatus
  ppkEmployeeBp: number | null
  ppkEmployerBp: number | null
  kupVariant: KupVariant
  taxCreditDivisor: TaxCreditDivisor | null
  youthReliefWaived: boolean
  /** Ulga z art. 21 ust. 1 pkt 152–154 zadeklarowana przez pracownika (Etap 32). */
  taxReliefKind: TaxReliefKind | null
  /** Ostatni rok obowiązywania ulgi; ulga na powrót ma cztery lata. */
  taxReliefUntilYear: number | null
  mandateSicknessOptIn: boolean
  mandateStudentExempt: boolean
  /** Zakres ubezpieczenia zlecenia: pełny (ZUA) albo tylko zdrowotne (ZZA) — #80. */
  insuranceScope?: InsuranceScope
  /** Rodzaj umowy, z którego serwer liczy tytuł i waliduje zakres (warunki obowiązujące dziś). */
  insuranceContractType?: ContractType | null
  /** Ryczałt z art. 30 ust. 1 pkt 5a (umowa do progu roku) — bez kosztów uzyskania. */
  flatRateTax?: boolean
  /** Czy PPK obejmuje osobę (isPpkCovered silnika); `null` = nie wiadomo. */
  ppkCovered?: boolean | null
  /** Koszty autorskie 50 % zamiast 20 % (art. 22 ust. 9 pkt 3 PIT). */
  authorCosts?: boolean
  /** Kod tytułu ubezpieczenia do raportów imiennych; `null` = domyślny. */
  insuranceCode: string | null
  /** Kod wykonywanego zawodu do zgłoszenia ZUS ZUA (Etap 16). */
  occupationCode: string | null
  /** Dni chorobowego u poprzedniego pracodawcy w danym roku (świadectwo pracy). */
  sickPayDaysBefore: number
  sickPayDaysBeforeYear: number | null
  /** Prawo do zasiłku od pierwszego dnia — bez okresu wyczekiwania. */
  benefitFromFirstDay: boolean
  /** Rezydencja podatkowa — fakt z akt (art. 3 ust. 1a i 2a PIT, Etap 48). */
  taxResidence: TaxResidence
  /** Kod kraju rezydencji (ISO 3166-1 alpha-2) do informacji IFT-1R. */
  residenceCountry: string | null
  /** Zagraniczny numer identyfikacyjny podatnika — z API ZAMASKOWANY. */
  foreignTin: string | null
  residenceCertificateFrom: string | null
  residenceCertificateTo: string | null
  /** Stawka z umowy o unikaniu podwójnego opodatkowania (punkty bazowe). */
  treatyTaxBp: number | null
}

export interface ZusRegistrationItem {
  id: string | null
  /** ZZA i ZIUA doszły w Etapie 25, ZCNA w Etapie 30. */
  kind: 'zua' | 'zza' | 'zwua' | 'ziua' | 'zcna'
  /** Tryb z bloku I formularza: zgłoszenie, zmiana danych albo korekta (Etap 31). */
  mode: 'zgloszenie' | 'zmiana' | 'korekta'
  setNumber: number
  /** Co poprawia ten dokument; `null` przy pierwotnym zgłoszeniu. */
  correctionScope: string | null
  employeeId: string
  employeeName: string
  employeeNo: number | null
  /** Którego członka rodziny dotyczy wiersz ZCNA; `null` przy innych (Etap 37). */
  memberLabel?: string | null
  /** ZIUA nie dotyczy tytułu ubezpieczenia — kod jest wtedy pusty. */
  insuranceCode: string | null
  effectiveOn: string
  deadline: string
  status: 'todo' | 'gen' | 'sent' | 'err'
  reasonCode: string | null
  hasPesel: boolean
  issues: Array<{ kind: string; params: Record<string, string | number>; blocking: boolean }>
  generatedAt: string | null
  sentAt: string | null
  /** ZIUA: pola, które się zmieniły, oraz stan przed i po (bez numerów PESEL). */
  identityChange?: {
    fields: string[]
    previous: { pesel: string | null; lastName: string; firstName: string; birthDate: string | null }
    next: { pesel: string | null; lastName: string; firstName: string; birthDate: string | null }
  } | null
}

export interface ZusRegistrationsResponse {
  items: ZusRegistrationItem[]
  pendingCount: number
  /** Wystawione z brakami — „Wystaw zgłoszenia" składa je od nowa (#8). */
  recomputeCount?: number
  overdueCount: number
  canRun: boolean
}

export interface PayrollProfileInput {
  ppkStatus: PpkStatus
  ppkEmployeeBp?: number | null
  ppkEmployerBp?: number | null
  kupVariant: KupVariant
  taxCreditDivisor?: TaxCreditDivisor | null
  youthReliefWaived: boolean
  /** Ulga z art. 21 ust. 1 pkt 152–154 zadeklarowana przez pracownika (Etap 32). */
  taxReliefKind: TaxReliefKind | null
  /** Ostatni rok obowiązywania ulgi; ulga na powrót ma cztery lata. */
  taxReliefUntilYear: number | null
  mandateSicknessOptIn: boolean
  mandateStudentExempt: boolean
  /** Wysyłane tylko wtedy, gdy pole jest widoczne (zlecenie bez statusu studenta). */
  insuranceScope?: InsuranceScope
  /** Koszty autorskie 50 % zamiast 20 % (art. 22 ust. 9 pkt 3 PIT). */
  authorCosts?: boolean
  insuranceCode?: string | null
  occupationCode?: string | null
  sickPayDaysBefore?: number
  sickPayDaysBeforeYear?: number | null
  benefitFromFirstDay?: boolean
  /* Rezydencja podatkowa i certyfikat (Etap 48, ADR-0049). */
  taxResidence?: TaxResidence
  residenceCountry?: string | null
  /**
   * Nowy numer podatnika w postaci jawnej — pole wysyłamy TYLKO wtedy, gdy
   * ktoś je zmienił: z API wraca maska, więc jej odesłanie nadpisałoby numer
   * kropkami.
   */
  foreignTin?: string | null
  residenceCertificateFrom?: string | null
  residenceCertificateTo?: string | null
  treatyTaxBp?: number | null
}

export interface PortalPayItem {
  id: string
  month: string
  label: string | null
  grossGr: number
  netGr: number
  /** Do wypłaty po potrąceniach (D108) — różni się od netto przy zajęciu albo karze. */
  payoutGr: number
  /** Wynagrodzenie chorobowe i zasiłek w tym pasku — tłumaczą niższe brutto. */
  sickPayGr: number
  benefitGr: number
  sickDays: number
  payoutDate: string | null
  /** Rodzaj umowy paska — B2B to rozliczenie umowy, nie pasek (D93). */
  contractType?: ContractType | null
  status: PayslipStatus
}

export interface PortalPayResponse {
  engineAvailable: boolean
  items: PortalPayItem[]
}

// --- Moduły HR (Etap 6) -----------------------------------------------------

export interface HrDashboardResponse {
  kpi: {
    employees: number
    activeEmployees: number
    onboarding: number
    openRequests: number
    endingContracts: number
    missingDocuments: number
    documentsToSign: number
  }
  /** Pozycje powstają z faktów — pusta firma dostaje pustą listę [D19]. */
  attention: Array<{
    key: string
    count: number
    meta?: string
    /** Miesiąc „rrrr-mm" — ekran formatuje go sam (fala 9, grupa 151). */
    month?: string
    person?: string
    date?: string
  }>
  /**
   * „Następne kroki" (spec 01 §1 pkt 4; fala 8, grupa 118) — też z faktów.
   * Stara odpowiedź (fikstura sprzed fali 8) ich nie ma.
   */
  nextSteps?: Array<{ key: string; count: number; meta?: string; deadline?: string | null }>
  /**
   * Ostatnia lista płac. `available: false` = żadnej jeszcze nie naliczono,
   * więc kafel nie pokazuje kwoty (silnik jest, danych nie ma).
   */
  payroll:
    | { available: false }
    | {
        available: true
        id: string
        month: string
        label: string | null
        status: PayrollPeriodStatus
        peopleCount: number
        employerCostGr: number | null
        warningCount: number
      }
}

export interface HealthRecordItem {
  employeeId: string
  displayId: string
  firstName: string
  lastName: string
  position: string | null
  /** W procesie zatrudnienia skierowania wystawia proces, nie moduł Badania. */
  onboarding: boolean
  /*
   * Stan z rekordu rozstrzygającego, a skierowanie w toku osobno z terminem
   * (fala 8, grupy 26–28, ADR-0058).
   */
  medical: {
    validity: HealthState
    examDate: string | null
    validUntil: string | null
    pending: { id: string; dueOn: string | null } | null
  }
  bhp: {
    validity: HealthState
    kind: string | null
    performedAt: string | null
    validUntil: string | null
    pending: { id: string; kind: string; dueOn: string | null } | null
  }
}

export interface TimePeriodResponse {
  month: string
  status: 'open' | 'review' | 'closed'
  normHours: number
  /*
   * Kafle z zestawienia silnika, jak lista płac (fala 7b, #36): nadgodziny
   * z rozkładu, pora nocna z okna firmy i braki per osoba — do dziś.
   */
  exceptions: {
    overtimeHours: number
    overtimePeople: number
    nightHours: number
    nightPersonDays: number
    missingDays: number
    missingPeople: number
    unapprovedEntries: number
  }
  days: Array<{
    date: string
    weekend: boolean
    holiday: boolean
    hours: number
    kinds: string[]
    missing: boolean
    missingPeople: number
    overtimeHours: number
    nightHours: number
  }>
}

export interface TeamLeaveItem {
  id: string
  typeKey: string
  typeLabel: string
  dateFrom: string
  dateTo: string
  status: string
  firstName: string
  lastName: string
}

/** Urlop z wniosku w kalendarzu zespołu. */
export interface TeamCalendarLeave extends TeamLeaveItem {
  kind: 'leave'
}

/**
 * Zwolnienie z rejestru w kalendarzu zespołu (grupa 149, ADR-0012 §7): sam
 * fakt nieobecności, bez przyczyny i rodzaju świadczenia. „sick" dostaje rola,
 * która czyta rejestr, „absence" — przełożony.
 */
export interface TeamCalendarAbsence {
  id: string
  kind: 'sick' | 'absence'
  dateFrom: string
  dateTo: string
  firstName: string
  lastName: string
}

export type TeamCalendarItem = TeamCalendarLeave | TeamCalendarAbsence

export interface TeamCalendarResponse {
  from: string
  to: string
  /** Święta ustawowe z tygodnia (holidays_pl). */
  holidays: string[]
  items: TeamCalendarItem[]
}

export interface PendingLeaveItem extends TeamLeaveItem {
  days: number
  note: string | null
  createdAt: string
  employeeId: string
  /** Ostatni dzień zatrudnienia osoby, gdy zapisano ustanie z datą (grupa 44). */
  employmentEndAt?: string | null
  /** Wniosek sięga poza ostatni dzień zatrudnienia — akceptacji serwer odmówi. */
  afterEmploymentEnd?: boolean
}

export interface TeamBalanceItem {
  employeeId: string
  firstName: string
  lastName: string
  /** Umowa cywilna — urlop nie przysługuje, limitu nie ma (D102). */
  notApplicable?: boolean
  limitDays: number | null
  usedDays: number
  remainingDays: number | null
  limitHours?: number | null
  usedHours?: number | null
  remainingHours?: number | null
  proportionalMonths?: number | null
  baseAssumed?: boolean
  fteAssumed?: boolean
  /** Okres wypowiedzenia: ostatni dzień zatrudnienia. */
  employmentEndAt?: string | null
}

export interface HrDocumentItem {
  id: string
  type: string
  title: string
  status: 'szkic' | 'do_akceptacji' | 'do_podpisu' | 'podpisany' | 'zarchiwizowany'
  date: string | null
  signedAt: string | null
  fileId: string | null
  /** Zapisana treść dokumentu — PDF powstaje przy pobraniu, jeśli pliku jeszcze nie ma (grupa 48). */
  hasContent: boolean
  employeeId: string | null
  firstName: string | null
  lastName: string | null
}

export interface HrDocumentsResponse {
  items: HrDocumentItem[]
  counts: Record<string, number>
  generatableTypes: string[]
}

/**
 * Członek rodziny w ubezpieczeniu zdrowotnym (Etap 30). Numer PESEL przychodzi
 * ZAMASKOWANY — pełny opuszcza serwer wyłącznie w pliku KEDU.
 */
export interface FamilyMemberItem {
  id: string
  firstName: string
  lastName: string
  pesel: string | null
  documentKind: string | null
  documentNumber: string | null
  birthDate: string | null
  kinshipCode: string
  kinshipName: string | null
  sharedHousehold: boolean
  disabilityCode: string
  address: {
    postalCode: string
    city: string | null
    street: string | null
    houseNo: string | null
    flatNo: string | null
  } | null
  entitlementFrom: string
  entitlementTo: string | null
  /** Dzień, w którym pracownik poinformował płatnika — od niego 7 dni. */
  declaredAt: string
  endDeclaredAt: string | null
  /** Stan dokumentu ZUS ZCNA zgłoszenia i utraty uprawnień (grupa 139). */
  zcna?: ZcnaState
  zcnaEnd?: ZcnaState | null
}

/** Stan ZCNA bez szczegółów zastrzeżeń — portal mówi tylko „czeka" albo „zgłoszono". */
export interface ZcnaState {
  status: 'todo' | 'gen' | 'err' | 'sent' | 'na'
  /** Termin dla firmy: siedem dni od dnia poinformowania płatnika. */
  deadline: string
  sentAt: string | null
}

export interface FamilyMembersResponse {
  items: FamilyMemberItem[]
  /** Czy osoba jest ubezpieczona u tego płatnika — bez tego ZCNA nie powstanie (grupa 166). */
  insured?: boolean
}

export interface FamilyMemberInput {
  firstName: string
  lastName: string
  pesel?: string
  documentKind?: '1' | '2'
  documentNumber?: string
  birthDate?: string
  kinshipCode: string
  sharedHousehold?: boolean
  disabilityCode?: string
  address?: {
    postalCode: string
    city: string
    street?: string
    houseNo: string
    flatNo?: string
  }
  entitlementFrom: string
  declaredAt: string
}

/** Dzień rozkładu czasu pracy; puste godziny znaczą dzień wolny (Etap 41). */
export interface ScheduleDayInput {
  date: string
  startsAt: string | null
  endsAt: string | null
}

/** Umowa o dzieło w rejestrze zgłoszeń RUD (Etap 47, ADR-0048). */
export interface DzieloContractItem {
  id: string
  employeeId: string
  employeeName: string
  concludedOn: string
  startsOn: string
  endsOn: string
  subject: string
  amountGr: number | null
  reportedOn: string | null
  correction: boolean
  note: string | null
  /** Termin z art. 36 ust. 17 u.s.u.s.: 7 dni od zawarcia umowy. */
  deadline: string
  status: 'todo' | 'overdue' | 'reported' | 'reported_late'
}

export interface DzieloRegisterResponse {
  year: number
  items: DzieloContractItem[]
  /** Osoby z umową o dzieło w warunkach, ale bez wpisu w rejestrze. */
  missingRegister: Array<{ employeeId: string; employeeName: string }>
  maxContractsPerForm: number
}

export interface DzieloContractInput {
  employeeId: string
  concludedOn: string
  startsOn: string
  endsOn: string
  subject: string
  amountGr?: number
  correction?: boolean
  note?: string | null
}

/**
 * Wniosek podatnika o informację IFT-1 (art. 42 ust. 4 ustawy o PIT, Etap 52).
 * Termin czternastu dni biegnie od dnia ZŁOŻENIA wniosku, nie od jego wpisania.
 */
export interface IftRequestItem {
  id: string
  employeeId: string
  employeeName: string
  employeeNo: number
  requestedOn: string
  year: number
  /** Dzień złożenia plus czternaście dni. */
  deadline: string
  issuedOn: string | null
  documentId: string | null
  note: string | null
  state: 'do_wystawienia' | 'wystawiona' | 'po_terminie'
}

export interface IftRequestInput {
  employeeId: string
  requestedOn: string
  year: number
  note?: string
}

/** Granice pracy nadliczbowej: art. 151 § 3 i art. 131 § 1 k.p. (Etap 45). */
export interface OvertimeLimitsView {
  annualLimitHours: number
  rescueHours: number
  managingEmployee: boolean
  /** Notatka kadr przy granicach roku — formularz zapisuje ją razem z resztą (grupa 9). */
  note: string | null
  yearOvertimeHours: number
  annual: {
    countedHours: number
    limitHours: number
    remainingHours: number
    exceededHours: number
  }
  averageWeeklyHours: number
  periodLimitHours: number
  periodWorkedHours: number
  periodExceededHours: number
  periodClosed: boolean
}

/** Ryczałty z art. 151(1) § 4 i art. 151(8) § 2 k.p. (Etap 51). */
export interface LumpSumView {
  /** Czy pracownik wykonuje stale pracę poza zakładem — warunek obu ryczałtów. */
  outsideWorkplace: boolean
  overtimeLumpSumGr: number | null
  nightLumpSumGr: number | null
}

export interface OvertimeLimitsInput {
  year: number
  annualLimitHours: number
  rescueHours?: number
  managingEmployee?: boolean
  /* Ryczałty (Etap 51): wolno je zapisać tylko przy pracy poza zakładem. */
  outsideWorkplace?: boolean
  overtimeLumpSumGr?: number | null
  nightLumpSumGr?: number | null
  note?: string | null
}

/** System czasu pracy pracownika i okres rozliczeniowy (Etap 44, ADR-0045). */
export type WorkTimeSystem =
  | 'podstawowy'
  | 'rownowazny'
  | 'rownowazny_dozor'
  | 'rownowazny_pilnowanie'
  | 'skrocony_tydzien'
  | 'weekendowy'
  | 'zadaniowy'

export interface SettlementPeriodView {
  from: string
  to: string
  months: number
  /** Czy ten miesiąc jest ostatnim miesiącem okresu — wtedy rozliczamy normę. */
  isLast: boolean
}

export interface WorkTimeResponse {
  system: WorkTimeSystem
  settlementMonths: number
  settlementStart: string
  art129Extension: boolean
  employeeRequestOn: string | null
  /** Czy firma zapisała ustawienia, czy liczymy domyślnymi (podstawowy, miesiąc). */
  stored: boolean
  month: string
  period: SettlementPeriodView
  rules: {
    maxDailyHours: number
    baseSettlementMonths: number
    tracksHours: boolean
    restEqualsShift: boolean
    employeeRequest: boolean
  }
  maxSettlementMonths: number
}

export interface WorkTimeInput {
  system: WorkTimeSystem
  settlementMonths: number
  settlementStart: string
  art129Extension?: boolean
  employeeRequestOn?: string | null
  note?: string | null
}

export interface ScheduleResponse {
  id: string | null
  month: string
  publishedOn: string | null
  /** Termin przekazania rozkładu pracownikowi — art. 129 § 3 k.p. */
  handoverDeadline: string
  /** Termin minął, a rozkładu nie przekazano (fala 7b, grupa 124). */
  handoverOverdue: boolean
  /** Wymiar czasu pracy okresu z art. 130 k.p. */
  normHours: number
  /** Wymiar pracownika z etatu — od niego liczy się „ponad wymiar" (fala 7b, #25). */
  employeeNormHours: number
  fteBp: number
  /** Czy rozkład dotyczy osoby — przy umowie cywilnej nie (fala 7b, #35). */
  applies: boolean
  plannedHours: number
  /** System czasu pracy pracownika i najdłuższa dopuszczalna zmiana (Etap 44). */
  system: WorkTimeSystem
  maxDailyHours: number
  period: SettlementPeriodView
  /** Dni rozkładu ze znacznikiem święta (fala 7b, grupa 125). */
  days: Array<ScheduleDayInput & { holiday?: boolean }>
  issues: Array<{ kind: string; date: string | null; hours: number | null }>
}

/**
 * Kara porządkowa z art. 108–113 k.p. (Etap 38). Wiersz niesie całe
 * postępowanie: naruszenie, terminy, sprzeciw i skutek płacowy.
 */
export interface PenaltyItem {
  id: string
  employeeId: string
  kind: string
  offence: string
  description: string
  offenceOn: string
  learnedOn: string
  hearingOn: string | null
  appliedOn: string
  notifiedOn: string | null
  amountGr: number | null
  /** Granica z art. 108 § 3 przyjęta przy wymierzeniu kary. */
  dailyWageGr: number | null
  status: string
  objectedOn: string | null
  /** Do kiedy pracownik może wnieść sprzeciw (art. 112 § 1). */
  objectionDeadline: string | null
  /** Kiedy milczenie pracodawcy stanie się uwzględnieniem sprzeciwu. */
  objectionSilenceOn: string | null
  objectionDecidedOn: string | null
  /** Uzasadnienie sprzeciwu — słowa pracownika. */
  objectionComment: string | null
  /** Uzasadnienie decyzji pracodawcy o sprzeciwie. */
  objectionDecisionComment: string | null
  /** Decyzja sprzed migracji 0063 nadpisała `objectionComment` — treść niejednoznaczna. */
  objectionCommentLegacy?: boolean
  /** Kto rozstrzygnął sprzeciw: kadry albo milczenie z art. 112 § 1. */
  objectionDecidedBy: 'kadry' | 'milczenie' | null
  /** Czy pracownik może jeszcze wnieść sprzeciw — liczy serwer. */
  objectionOpen: boolean
  /** Kiedy wysłano zawiadomienie o odrzuceniu sprzeciwu (art. 112 § 2). */
  objectionRejectionNotifiedAt: string | null
  /** Ostatni dzień na odwołanie do sądu pracy — 14 dni od zawiadomienia. */
  courtDeadline: string | null
  /** Podstawa uznania kary za niebyłą (art. 113 § 1). */
  voidBasis: PenaltyVoidBasis | null
  refundedGr: number
  deductedGr: number
  /** Dzień zatarcia kary — rok od zastosowania (art. 113 § 1). */
  voidOn: string | null
  documentId: string | null
}

export interface PenaltyInput {
  kind: string
  offence: string
  description: string
  offenceOn: string
  learnedOn: string
  hearingOn?: string
  appliedOn: string
  amountGr?: number
}

/**
 * Tytuł do potrącenia z wynagrodzenia (Etap 33). Granice i kwotę wolną liczy
 * silnik — tu jest sam fakt z pisma.
 */
export interface GarnishmentItem {
  id: string
  kind: GarnishmentKind
  creditor: string
  caseNumber: string | null
  /** Dzień doręczenia wezwania — od niego biegnie tydzień z art. 882 § 1 k.p.c. */
  servedOn: string | null
  /** Termin oświadczenia policzony przez serwer; `null` bez daty doręczenia. */
  statementDeadline: string | null
  /** Data wystawienia oświadczenia; `null` = jeszcze go nie ma. */
  statementIssuedOn: string | null
  /** Odbiorca przelewu z wezwania; puste = ten sam, co wierzyciel (Etap 34). */
  payeeName: string | null
  bailiffAccount: string | null
  totalDueGr: number | null
  monthlyDueGr: number | null
  effectiveFrom: string
  effectiveTo: string | null
  /** Dzień pierwszego przelewu — art. 881 § 3 pkt 1 k.p.c. */
  firstTransferOn: string | null
  note: string | null
}

export interface GarnishmentInput {
  kind: GarnishmentKind
  creditor: string
  caseNumber?: string
  servedOn?: string
  payeeName?: string
  bailiffAccount?: string
  totalDueGr?: number
  monthlyDueGr?: number
  effectiveFrom: string
  note?: string
}

export interface WorkflowItem {
  id: string
  name: string
  description: string | null
  chain: string[]
  status: 'active' | 'draft'
}

export interface DevelopmentItem {
  id: string
  title: string
  form: string | null
  costGr: number | null
  progressPct: number | null
  dueDate: string | null
  status: 'wniosek' | 'zatwierdzone' | 'w_trakcie' | 'ukonczone'
  employeeId: string
  firstName: string
  lastName: string
}

export interface DevelopmentResponse {
  year: number
  items: DevelopmentItem[]
  budget: { limitGr: number; usedGr: number } | null
}

export interface ReportsSummary {
  payrollCostGr: number
  byDepartment: Array<{ department: string; costGr: number }>
  headcount: { active: number; onboarding: number; terminated: number }
  leaveDays: { approved: number; pending: number }
  /** Nadgodziny z zatwierdzonej ewidencji, jak lista płac (fala 7b, grupa 110). */
  overtimeHours: number
  /** Miesiąc kafla „Nadgodziny" (RRRR-MM). */
  overtimeMonth: string
  /**
   * Koszt pracodawcy per miesiąc WYPŁATY z ZAMKNIĘTYCH list płac (D29).
   * Miesiące bez zamkniętej listy nie występują — wykres pokazuje to, co
   * policzono, a nie zera udające koszt zerowy.
   */
  monthlyTrend: Array<{ month: string; costGr: number }>
}

export interface SettingsTile {
  key: string
  status: 'configured' | 'attention' | 'optional' | 'soon'
  count?: number
  /** „Słowniki": włączone kafelki portalu (grupa 37). */
  requestTypesEnabled?: number
}

export interface EmployeeAuditItem {
  id: number
  action: string
  objectType: string
  actorLabel: string
  createdAt: string
  result: 'ok' | 'deny'
  /** Pole wniosku o zmianę danych, którego dotyczy zdarzenie. */
  field: ChangeRequestField | null
  /** Porównanie przed/po (§17 pkt 2) — tylko w zakresie roli. */
  change: { before: string | null; after: string | null } | null
  /** Szczegół zdarzenia, jeśli rola widzi tę sekcję akt (np. data aneksu). */
  detail: string | null
}

/** Zdarzenie dziennika audytu (spec 01 §14). */
export interface AuditItem {
  id: number
  seq: number
  actorLabel: string
  action: string
  objectType: string
  objectLabel: string
  module: string
  permission: string
  result: 'ok' | 'deny'
  createdAt: string
}

export interface AuditResponse {
  items: AuditItem[]
  nextCursor: number | null
  filters: { actors: string[]; objectTypes: string[]; actions: string[]; modules: string[] }
  /** Wynik bieżących filtrów (grupa 144). */
  totals: { all: number; denied: number }
  /** Liczba wszystkich zdarzeń firmy — odróżnia pusty dziennik od pustego wyniku filtra. */
  journalTotal: number
}

/** Wynik przeliczenia dowodu integralności — `valid` liczy serwer, nie UI. */
export interface AuditProof {
  id: number
  seq: number
  hash: string
  prevHash: string
  recomputedHash: string
  hashMatches: boolean
  linkedToPrevious: boolean
  valid: boolean
  chain: { length: number; headHash: string | null }
}

/** Okres przechowywania akt i stan anonimizacji (Etap 20, ADR-0021). */
export interface DataProtectionView {
  employed: boolean
  endDate?: string
  anonymizedAt: string | null
  retention: {
    years: number
    endsOn: string
    basis: 'kp-94-9b' | 'fus-125a'
    canBeShortenedByRia: boolean
    assumedLongest: boolean
    expired: boolean
  } | null
  canAnonymize: boolean
}

/** PIT-4R: roczna deklaracja płatnika o zaliczkach (Etap 22, ADR-0023). */
export interface Pit4rView {
  year: number
  summary: {
    year: number
    months: Array<{
      month: string
      number: number
      row1TaxpayerCount: number
      row1Gr: number
      row3Gr: number
      row5Gr: number
      row10Gr: number
      row12Gr: number
    }>
    totalGr: number
    maxTaxpayers: number
    deadline: string
  }
  closedMonths: string[]
  /** Czy rok podatkowy już się skończył — w trakcie roku wystawienie wymaga potwierdzenia (#70). */
  yearClosed: boolean
  document: {
    id: string
    setNumber: number
    status: string
    generatedAt: string | null
    sentAt: string | null
    /** Zastrzeżenia zapisane przy wystawieniu, np. `rok_niezamkniety` (#70). */
    issues: DeclarationIssue[]
    /** Wystawiony przed końcem roku, który już minął — do wystawienia ponownie. */
    stale: boolean
  } | null
}

/** Rozliczenie końcowe: ekwiwalent za urlop i odprawa (Etap 23, ADR-0024). */
export interface SettlementProposal {
  employeeId: string
  terminationId: string
  endDate: string
  year: number
  leaveEquivalent: {
    unusedDays: number
    unusedHours: number
    /** Wymiar roku ustania po etacie i proporcji z art. 155(1) k.p. (#6). */
    entitlementDays: number
    fullYearDays: number
    /** Miesiące proporcji; `null` = pełny wymiar roku. */
    proportionalMonths: number | null
    usedHours: number
    /** Wymiar 26 dni ze słownika — do potwierdzenia w aktach. */
    baseAssumed: boolean
    /** Etat z akt przyjęty na cały rok (brak historii warunków). */
    fteAssumed: boolean
    /** Wymiar z art. 154 § 1 ze słownika — do noty. */
    baseDays: number
    coefficientCenti: number
    baseGr: number
    perDayGr: number
    perHourGr: number
    amountGr: number
  } | null
  severance: {
    tenureMonths: number
    /** Od kiedy liczony staż — najwcześniejsza umowa albo data z akt (grupa 104 z raportu testu). */
    tenureFrom: string
    months: number
    monthlyGr: number
    /** `null` = pułapu z art. 8 ust. 4 nie ustalono (brak minimalnego roku ustania). */
    capGr: number | null
    capped: boolean
    amountGr: number
  } | null
  /** Powód braku odprawy; `null` = odprawa policzona. */
  severanceUnavailable: 'no_base_salary' | 'no_tenure_start' | 'not_employment' | null
  openPeriods: Array<{ id: string; month: string }>
  /** `fromSettlement` = pozycja z tego rozliczenia; drugi raz się nie dopisze (#67). */
  applied: Array<{
    kind: string
    amountGr: number
    month: string
    fromSettlement: boolean
    periodStatus: string
  }>
}

/** PIT-8AR: roczna deklaracja o zryczałtowanym podatku (Etap 24, ADR-0025). */
/** Pozycja rozwoju w portalu: cel, ocena albo szkolenie tej osoby. */
export interface PortalGrowthItem {
  id: string
  title: string
  form: string | null
  costGr: number | null
  progressPct: number | null
  dueDate: string | null
  status: DevItemStatus
}

export interface Pit8arView {
  year: number
  summary: {
    year: number
    months: Array<{
      month: string
      number: number
      /** Wiersz 1: podatek u źródła od nierezydentów (art. 29, Etap 52). */
      row1Gr: number
      row6Gr: number
      row36Gr: number
      row38Gr: number
    }>
    totalGr: number
    deadline: string
  }
  closedMonths: string[]
  /** Czy rok podatkowy już się skończył — w trakcie roku wystawienie wymaga potwierdzenia (#70). */
  yearClosed: boolean
  /**
   * Ile z deklaracji to podatek u źródła od nierezydentów — wiersz 1 formularza
   * (art. 29 ust. 1 pkt 1). Od Etapu 52 kwota JEST w deklaracji; pokazujemy ją
   * osobno, bo za ten sam podatek wychodzi imienna informacja (ADR-0053).
   */
  withholdingTaxGr: number
  document: {
    id: string
    setNumber: number
    status: string
    generatedAt: string | null
    sentAt: string | null
    /** Zastrzeżenia zapisane przy wystawieniu, np. `rok_niezamkniety` (#70). */
    issues: DeclarationIssue[]
    /** Wystawiony przed końcem roku, który już minął — do wystawienia ponownie. */
    stale: boolean
  } | null
}

/**
 * Nazwa pliku z nagłówka Content-Disposition (RFC 6266): najpierw `filename*`
 * w UTF-8 z pełną polską nazwą, potem zastępnik ASCII z `filename` (grupa 164).
 */
export function filenameFrom(disposition: string): string | null {
  /*
   * Serwer wypisuje `filename*` jako ostatni parametr, a wartość kodowana
   * procentowo nie ma średnika, cudzysłowu ani spacji. Bez kotwicy regex łapał
   * ciąg „filename*=…" z wnętrza cytowanego zastępnika ASCII (zgłoszenie 25).
   */
  const extended = /;\s*filename\*=UTF-8''([^;"\s]+)\s*$/i.exec(disposition)
  if (extended) {
    try {
      return decodeURIComponent(extended[1]!.trim())
    } catch {
      /* uszkodzone kodowanie — spróbujmy zwykłej nazwy */
    }
  }
  const plain = /(?:^|;)\s*filename="([^"]+)"/i.exec(disposition)
  return plain ? plain[1]! : null
}

/**
 * Odpowiedź z plikiem → pobranie w przeglądarce; odmowa zostaje `ApiError`.
 * Ścieżkę składa wywołujący w kliencie — ekrany nie znają adresów API, bo
 * obraz demonstracyjny rozgałęzia się tylko tutaj (ADR-0059).
 */
async function saveDownload(response: Response, fallbackName: string): Promise<void> {
  if (!response.ok) {
    throw apiErrorFrom(response.status, await response.json().catch(() => null), 'UNKNOWN', 'Nie udało się pobrać pliku')
  }
  const named = filenameFrom(response.headers.get('content-disposition') ?? '')
  const url = URL.createObjectURL(await response.blob())
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = named ?? fallbackName
    link.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Pobranie załącznika przez fetch; błąd API zostaje ApiError z kodem. */
async function downloadAttachment(url: string, fallbackName: string): Promise<void> {
  const response = await sendUrl(url, { credentials: 'same-origin' })
  if (!response.ok) {
    throw apiErrorFrom(response.status, await response.json().catch(() => null), 'UNKNOWN', 'Nie udało się pobrać pliku')
  }
  const disposition = response.headers.get('content-disposition') ?? ''
  const named = filenameFrom(disposition)
  const objectUrl = URL.createObjectURL(await response.blob())
  try {
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = named ?? fallbackName
    link.click()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const api = {
  authEntry: (signal?: AbortSignal) =>
    apiRequest<AuthEntryResponse>('/auth/entry', signal ? { signal } : {}),
  session: () => apiRequest<SessionResponse>('/auth/session'),
  /** Język konta z przełącznika — czytają go maile (fala 9, grupa 76). */
  updateLocale: (locale: 'pl' | 'en') =>
    apiRequest<void>('/auth/session/locale', { method: 'PUT', body: { locale } }),
  logout: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
  employees: (query = '') =>
    apiRequest<EmployeeListResponse>(`/employees${query ? `?${query}` : ''}`),
  employee: (id: string) => apiRequest<EmployeeProfileResponse>(`/employees/${id}`),
  employeeAudit: (id: string) =>
    apiRequest<{ items: EmployeeAuditItem[] }>(`/employees/${id}/audit`),
  /* Pozycje akt odhaczane ręcznie (PIT-2, ZUS ZUA) — reszta wynika z dowodu. */
  setFileChecklistItem: (employeeId: string, itemKey: string, done: boolean) =>
    apiRequest<{ itemKey: string; done: boolean }>(
      `/employees/${employeeId}/file-checklist/${itemKey}`,
      { method: 'PUT', body: { done } },
    ),
  revealEmployeeField: (id: string, field: 'pesel' | 'iban') =>
    apiRequest<{ field: string; value: string | null }>(`/employees/${id}/reveal`, {
      method: 'POST',
      body: { field },
    }),
  /* Portal pracownika — kontekst „moje dane" (spec 03). */
  meDashboard: () => apiRequest<PortalDashboardResponse>('/me/dashboard'),
  meDocuments: () => apiRequest<{ items: PortalDocumentItem[] }>('/me/documents'),
  meTime: (month?: string) =>
    apiRequest<PortalTimeResponse>(`/me/time${month ? `?month=${month}` : ''}`),
  meAddTimeEntry: (body: {
    date: string
    /** Liczba godzin — pomijana, gdy podano zakres „od–do" (Etap 39). */
    hours?: number
    /** Godzina rozpoczęcia i zakończenia pracy — wymóg ewidencji. */
    startsAt?: string
    endsAt?: string
    kind: TimeEntryKind
    note?: string
  }) => apiRequest<{ id: string; approved: boolean }>('/me/time-entries', { method: 'POST', body }),
  /**
   * Karta ewidencji czasu pracy za miesiąc (Etap 39) — dokument z § 6 pkt 1
   * lit. a rozporządzenia o dokumentacji pracowniczej, przechowywany osobno od
   * akt osobowych.
   */
  issueRecordCard: (body: { employeeId: string; month: string }) =>
    apiRequest<{
      id: string
      title: string
      totals: {
        workedHours: number
        nightHours: number
        overtimeHours: number
        workedDays: number
        leaveDays: number
        sickDays: number
        unjustifiedDays: number
        unjustifiedHours: number
        missingDays: number
        /** Dni wolne w zamian i godziny dyżurów z rejestrów (fala 7b, #47). */
        dayOffInLieuDays: number
        dutyHours: number
        missingClockTimes: boolean
        pendingApproval: boolean
      }
    }>('/time/record-card', { method: 'POST', body }),
  /** Wpis kadr do ewidencji — m.in. nieobecność nieusprawiedliwiona (Etap 39). */
  recordTimeEntry: (body: {
    employeeId: string
    date: string
    kind: TimeEntryKind
    hours?: number
    startsAt?: string
    endsAt?: string
    note?: string
  }) => apiRequest<{ id: string }>('/time/entries', { method: 'POST', body }),
  meLeaves: () => apiRequest<PortalLeavesResponse>('/me/leaves'),
  meSubmitLeave: (body: { typeKey: string; dateFrom: string; dateTo: string; note?: string }) =>
    apiRequest<{ id: string; days: number; status: string }>('/me/leaves', {
      method: 'POST',
      body,
    }),
  meHealth: () => apiRequest<PortalHealthResponse>('/me/health'),
  mePay: () => apiRequest<PortalPayResponse>('/me/pay'),
  meTax: () => apiRequest<PortalTaxResponse>('/me/tax'),
  /**
   * Rozwój pracownika — WŁASNE pozycje. Budżet roczny jest firmowy, więc portal
   * go nie pokazuje: pracownik widzi swoje szkolenia, nie limit działu kadr.
   */
  meGrowth: () => apiRequest<{ items: PortalGrowthItem[] }>('/me/growth'),

  /* Moduły HR (Etap 6). */
  hrDashboard: () => apiRequest<HrDashboardResponse>('/dashboard/hr'),
  healthRecords: () =>
    apiRequest<{ items: HealthRecordItem[]; counts: { all: number; warn: number; err: number } }>(
      '/health-records',
    ),
  referHealth: (employeeId: string, body: { kind: 'medical' | 'bhp'; deadlineDays: number }) =>
    apiRequest<{ referred: true; dueOn: string; renewed: boolean }>(`/health-records/${employeeId}/refer`, {
      method: 'POST',
      body,
    }),
  /** Wynik badania ze skierowania u zatrudnionego (fala 8, grupa 27). */
  recordMedicalResult: (id: string, body: { examDate: string; validUntil: string }) =>
    apiRequest<{ recorded: true }>(`/health-records/medical/${id}/result`, { method: 'POST', body }),
  /** Ukończenie szkolenia ze skierowania u zatrudnionego (fala 8, grupa 27). */
  completeBhpTraining: (id: string, body: { performedAt: string; conductedBy: string; validUntil?: string }) =>
    apiRequest<{ recorded: true }>(`/health-records/bhp/${id}/complete`, { method: 'POST', body }),
  timePeriod: (month: string) => apiRequest<TimePeriodResponse>(`/time/periods/${month}`),
  /* Wpisy zespołu do akceptacji — przełożony widzi swój zespół, HR cały tenant. */
  timeEntries: (month: string, pending = true) =>
    apiRequest<{ month: string; items: TimeApprovalEntry[] }>(
      `/time/entries?month=${encodeURIComponent(month)}&pending=${pending}`,
    ),
  approveTimeEntry: (id: string) =>
    apiRequest<{ approved: true }>(`/time/entries/${id}/approve`, { method: 'POST' }),
  /** Akceptacja zbiorcza — zakres jak przy pojedynczej (fala 7b, grupa 135). */
  approveTimeEntries: (ids: readonly string[]) =>
    apiRequest<{ approved: number; skipped: number }>('/time/entries/approve', {
      method: 'POST',
      body: { ids },
    }),
  /** `acknowledgeUnapproved` — zamknięcie z niezatwierdzonymi wpisami (grupa 136). */
  changeTimePeriod: (
    month: string,
    action: 'submit' | 'close' | 'reopen',
    acknowledgeUnapproved?: boolean,
  ) =>
    apiRequest<{ month: string; status: string }>(`/time/periods/${month}/${action}`, {
      method: 'POST',
      ...(acknowledgeUnapproved ? { body: { acknowledgeUnapproved: true } } : {}),
    }),
  teamCalendar: (from: string) =>
    apiRequest<TeamCalendarResponse>(`/leaves/team-calendar?from=${from}`),
  pendingLeaves: (status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending') =>
    apiRequest<{ items: PendingLeaveItem[] }>(`/leaves/pending?status=${status}`),
  decideLeave: (id: string, action: 'approve' | 'reject' | 'request-change', comment?: string) =>
    apiRequest<{ status: string }>(`/leaves/${id}/${action}`, { method: 'POST', body: { comment } }),
  teamBalances: () =>
    apiRequest<{ year: number; items: TeamBalanceItem[] }>('/leaves/team-balances'),
  hrDocuments: (status = 'all') =>
    apiRequest<HrDocumentsResponse>(`/documents?status=${status}`),
  /**
   * Szablony, które wolno wystawić osobie — ta sama reguła wersji warunków co
   * przy wystawieniu (przegląd Fali 5, zgłoszenie 42).
   */
  documentGeneratorOptions: (employeeId: string) =>
    apiRequest<{
      types: Array<{ type: string; allowed: boolean; contractType: ContractType | null }>
    }>(`/documents/generator-options?employeeId=${encodeURIComponent(employeeId)}`),
  generateDocument: (body: {
    employeeId: string
    type: string
    /** Świadectwo: pozycje spoza ewidencji wpisane w kreatorze (Fala 5, grupa 49). */
    certificate?: Partial<Record<string, string>>
  }) =>
    apiRequest<{ id: string; pdf: boolean }>('/documents', { method: 'POST', body }),
  documentTransition: (
    id: string,
    action: 'submit-for-approval' | 'approve' | 'sign' | 'archive',
  ) =>
    apiRequest<{ status: string }>(`/documents/${id}/${action}`, { method: 'POST' }),
  workflows: () => apiRequest<{ items: WorkflowItem[] }>('/workflows'),
  createWorkflow: (body: {
    name: string
    description?: string
    chain: string[]
    status: 'active' | 'draft'
  }) => apiRequest<{ id: string }>('/workflows', { method: 'POST', body }),
  toggleWorkflow: (id: string) =>
    apiRequest<{ status: 'active' | 'draft' }>(`/workflows/${id}/toggle`, { method: 'POST' }),
  development: () => apiRequest<DevelopmentResponse>('/development'),
  createDevelopmentItem: (body: {
    employeeId: string
    title: string
    form?: string
    costGr?: number
  }) => apiRequest<{ id: string }>('/development', { method: 'POST', body }),
  /** Zmiana statusu pozycji rozwoju; przy zatwierdzeniu stan budżetu roku (grupa 58). */
  setDevelopmentItemStatus: (id: string, status: DevItemStatus) =>
    apiRequest<{
      status: DevItemStatus
      budget: { year: number; limitGr: number; usedGr: number; exceeded: boolean } | null
    }>(`/development/${id}/status`, { method: 'POST', body: { status } }),
  setDevelopmentBudget: (year: number, limitGr: number) =>
    apiRequest<{ year: number; limitGr: number }>('/development/budget', {
      method: 'PUT',
      body: { year, limitGr },
    }),
  reportsSummary: () => apiRequest<ReportsSummary>('/reports/summary'),
  settingsTiles: () => apiRequest<{ tiles: SettingsTile[] }>('/settings/tiles'),
  /* Listy płac (Etap 9 — realny silnik naliczeń, P5). */
  payrollPeriods: () => apiRequest<PayrollPeriodsResponse>('/payroll/periods'),
  payrollPeriod: (id: string) => apiRequest<PayrollPeriodDetailResponse>(`/payroll/periods/${id}`),
  createPayrollPeriod: (body: { month: string; label?: string; payoutDate?: string }) =>
    apiRequest<{ id: string }>('/payroll/periods', { method: 'POST', body }),
  /** „Otwórz ponownie": zamknięta lista wraca do weryfikacji z powodem (Etap 13). */
  /** Ustanie zatrudnienia: data, tryb i strona inicjująca (Etap 15). */
  recordOffboarding: (
    employeeId: string,
    body: {
      endDate: string
      mode: TerminationMode
      initiator?: 'pracownik' | 'pracodawca'
      reason?: string
    },
  ) =>
    apiRequest<{
      endDate: string
      mode: TerminationMode
      zusMode: string
      legalBasis: string | null
      deregisterOn: string
    }>(`/employees/${employeeId}/offboarding`, { method: 'POST', body }),

  /* Aneks: kadry proponują, pracownik przyjmuje albo odrzuca (Etap 14). */
  proposeTermsChange: (employeeId: string, body: TermsChangeInput) =>
    apiRequest<{ id: string }>(`/employees/${employeeId}/terms-change`, {
      method: 'POST',
      body,
    }),
  withdrawTermsChange: (employeeId: string) =>
    apiRequest<{ status: 'withdrawn' }>(`/employees/${employeeId}/terms-change/withdraw`, {
      method: 'POST',
    }),
  portalTermsChange: () =>
    apiRequest<{
      proposal: {
        id: string
        startDate: string | null
        preparedBy: string | null
        changes: Array<{ field: string; before: string | null; after: string | null }>
      } | null
    }>('/portal/terms-change'),
  decideTermsChange: (decision: 'accept' | 'reject') =>
    apiRequest<{ status: string }>(`/portal/terms-change/${decision}`, { method: 'POST' }),

  /* Jednorazowe składniki okresu płacowego (Etap 14). */
  addPayrollExtra: (periodId: string, body: PayrollExtraInput) =>
    apiRequest<{ id: string; amountGr: number }>(`/payroll/periods/${periodId}/extras`, {
      method: 'POST',
      body,
    }),
  removePayrollExtra: (id: string) =>
    apiRequest<{ status: 'removed' }>(`/payroll/extras/${id}`, { method: 'DELETE' }),
  reopenPayrollPeriod: (id: string, reason: string) =>
    apiRequest<{
      status: PayrollPeriodStatus
      reopenCount: number
      settlementMonth: string
      correctionRequired: boolean
    }>(`/payroll/periods/${id}/reopen`, { method: 'POST', body: { reason } }),
  payrollAction: (id: string, action: 'calculate' | 'approve' | 'close') =>
    apiRequest<{ status?: string; peopleCount?: number }>(`/payroll/periods/${id}/${action}`, {
      method: 'POST',
    }),
  payslip: (id: string) => apiRequest<PayslipDetailResponse>(`/payroll/payslips/${id}`),
  payrollParameters: (year?: number) =>
    apiRequest<PayrollParametersResponse>(
      `/payroll/parameters${year === undefined ? '' : `?year=${year}`}`,
    ),
  savePayrollSettings: (year: number, body: PayrollSettingsInput) =>
    apiRequest<{ year: number }>(`/payroll/settings/${year}`, { method: 'PUT', body }),
  savePayrollPayer: (body: {
    nip?: string | null
    regon?: string | null
    /** Rachunki do pliku przelewów: firmy, ZUS (e-składka) i podatkowy (Etap 19). */
    bankAccount?: string | null
    zusAccount?: string | null
    taxAccount?: string | null
    ppkAccount?: string | null
    /** Kod urzędu skarbowego płatnika z wykazu MF (Etap 28). */
    taxOfficeCode?: string | null
  }) =>
    apiRequest<{
      nip: string | null
      regon: string | null
      bankAccount: string | null
      zusAccount: string | null
      taxAccount: string | null
    }>('/payroll/payer', {
      method: 'PUT',
      body,
    }),
  payrollProfiles: () => apiRequest<{ items: PayrollProfileItem[] }>('/payroll/profiles'),
  /* Rejestr zwolnień lekarskich (Etap 11) — chorobowe wchodzi na listę płac stąd. */
  sickLeaves: (year?: number) =>
    apiRequest<SickLeavesResponse>(
      `/sick-leaves${year === undefined ? '' : `?year=${year}`}`,
    ),
  registerSickLeave: (body: SickLeaveInput) =>
    apiRequest<{ id: string; days: number; correctionMonths: string[] }>('/sick-leaves', {
      method: 'POST',
      body,
    }),
  removeSickLeave: (id: string) =>
    apiRequest<void>(`/sick-leaves/${id}`, { method: 'DELETE' }),
  savePayrollProfile: (employeeId: string, body: PayrollProfileInput) =>
    apiRequest<{ employeeId: string }>(`/payroll/profiles/${employeeId}`, {
      method: 'PUT',
      body,
    }),
  /*
   * Ochrona danych (Etap 20): kopia danych schodzi plikiem, więc idzie przez
   * nawigację — pełny PESEL i numer rachunku nie krążą po stanie aplikacji.
   */
  employeeDataExportUrl: (employeeId: string) => `/api/v1/employees/${employeeId}/data-export`,
  myDataExportUrl: () => '/api/v1/me/data-export',
  /* Rejestr czynności przetwarzania (art. 30 RODO, Etap 26) — plik CSV. */
  processingRegisterUrl: () => '/api/v1/rodo/processing-register?format=csv',
  dataProtection: (employeeId: string) =>
    apiRequest<DataProtectionView>(`/employees/${employeeId}/data-protection`),
  anonymizeEmployee: (employeeId: string, reason: string) =>
    apiRequest<{ cleared: string[]; accountRemoved: boolean; documentsRetained: number }>(
      `/employees/${employeeId}/anonymize`,
      { method: 'POST', body: { reason } },
    ),

  /* Rozliczenie końcowe (Etap 23): propozycja kwot i dopisanie do listy płac. */
  settlement: (employeeId: string) =>
    apiRequest<SettlementProposal>(`/employees/${employeeId}/settlement`),
  applySettlement: (
    employeeId: string,
    body: {
      periodId: string
      includeEquivalent: boolean
      includeSeverance: boolean
      /** Rodzaje dopisywane mimo ręcznego składnika tego rodzaju (#67). */
      confirmKinds?: string[]
    },
  ) =>
    apiRequest<{ added: Array<{ kind: string; amountGr: number }> }>(
      `/employees/${employeeId}/settlement`,
      { method: 'POST', body },
    ),

  /**
   * Plik przelewów z zamkniętej listy płac (Etap 19, potrącenia — Etap 34).
   *
   * Idziemy `fetch`-em, nie nawigacją: plik niesie w nagłówkach podsumowanie —
   * ile zleceń, w tym ile potrąceń, i czego zabrakło (rachunku pracownika albo
   * rachunku odbiorcy z wezwania). Przy nawigacji te liczby ginęły, a to one
   * mówią, czy przelewy są kompletne.
   */
  downloadTransfers: async (
    periodId: string,
  ): Promise<{
    orders: number
    garnishments: number
    missing: number
    payeesMissing: number
    /** O ile blok V (zasiłki wypłacone przez płatnika) pomniejszył przelew ZUS. */
    zusBenefitsGr: number
    /** Część bloku V, która nie zmieściła się w składkach tej listy. */
    zusBenefitsUnappliedGr: number
    /** Wpłaty PPK bez zlecenia, bo nie ma rachunku instytucji (#85). */
    ppkUnpaidGr: number
    /** Wpłaty PPK policzone osobom bez deklaracji — poza plikiem. */
    ppkUndeclaredGr: number
    /** Termin wpłat PPK (art. 28 ustawy o PPK) albo `null`. */
    ppkDeadline: string | null
  }> => {
    const response = await send(`/payroll/periods/${periodId}/transfers`, {
      credentials: 'same-origin',
    })
    if (!response.ok) {
      throw apiErrorFrom(
        response.status,
        await response.json().catch(() => null),
        'UNKNOWN',
        'Nie udało się pobrać pliku przelewów',
      )
    }
    const disposition = response.headers.get('content-disposition') ?? ''
    const named = filenameFrom(disposition)
    const url = URL.createObjectURL(await response.blob())
    try {
      const link = document.createElement('a')
      link.href = url
      link.download = named ?? 'przelewy.txt'
      link.click()
    } finally {
      URL.revokeObjectURL(url)
    }
    const count = (header: string) => Number(response.headers.get(header) ?? '0')
    return {
      orders: count('x-k1p4-orders'),
      garnishments: count('x-k1p4-garnishments'),
      missing: count('x-k1p4-missing'),
      payeesMissing: count('x-k1p4-payees-missing'),
      zusBenefitsGr: count('x-k1p4-zus-benefits-gr'),
      zusBenefitsUnappliedGr: count('x-k1p4-zus-benefits-unapplied-gr'),
      ppkUnpaidGr: count('x-k1p4-ppk-unpaid-gr'),
      ppkUndeclaredGr: count('x-k1p4-ppk-undeclared-gr'),
      ppkDeadline: response.headers.get('x-k1p4-ppk-deadline') || null,
    }
  },

  /* Informacje dla ubezpieczonego i podatnika: ZUS IMIR i PIT-11 (Etap 18). */
  /*
   * `skipped` przy IMIR: osoby z paskiem, ale bez tytułu do ubezpieczeń u płatnika
   * — nie ma ich w raporcie imiennym, więc informacja ich nie dotyczy (#68).
   */
  issueImir: (period: string, acknowledgeOpenYear = false) =>
    apiRequest<{
      issued: number
      files: number
      period: string
      skipped: Array<{ employeeName: string; issue: string }>
    }>('/payroll/statements/imir', {
      method: 'POST',
      body: { period, ...(acknowledgeOpenYear ? { acknowledgeOpenYear } : {}) },
    }),
  /* `acknowledgeOpenYear`: świadome wystawienie za rok, który jeszcze trwa (#70). */
  issuePit11: (period: string, acknowledgeOpenYear = false) =>
    apiRequest<{ issued: number; files: number; period: string }>(
      '/payroll/statements/pit11',
      { method: 'POST', body: { period, ...(acknowledgeOpenYear ? { acknowledgeOpenYear } : {}) } },
    ),
  /*
   * IFT-1R (Etap 48): informacja dla nierezydenta o przychodzie i podatku
   * pobranym u źródła. `skipped` niesie osoby, którym brakuje kodu kraju —
   * bez niego informacja nie mówi, do którego państwa trafia (ADR-0049).
   */
  issueIft1r: (period: string, acknowledgeOpenYear = false) =>
    apiRequest<{
      issued: number
      files: number
      period: string
      skipped: Array<{ employeeName: string; issue: string }>
    }>('/payroll/statements/ift1r', {
      method: 'POST',
      body: { period, ...(acknowledgeOpenYear ? { acknowledgeOpenYear } : {}) },
    }),

  /**
   * Plik XML informacji PIT-11 do e-Deklaracji (Etap 27).
   *
   * Nie idziemy tu `location.assign`, jak przy PDF-ie: gdy w aktach brakuje kodu
   * urzędu albo daty urodzenia, serwer odpowiada 409 z listą braków — a tę listę
   * chcemy pokazać w ekranie, nie jako surowy JSON w nowej karcie.
   */
  /**
   * PDF dokumentu z kadr albo z portalu (Fala 5, grupa 48). Pobranie idzie
   * `fetch`-em, a nie nawigacją: plik może powstawać dopiero teraz z zapisanej
   * treści, a niedostępny serwis PDF (503) albo dokument bez treści (404) mają
   * dać komunikat na ekranie, nie surowy JSON w nowej karcie.
   */
  downloadHrDocumentPdf: async (documentId: string, fallbackName: string): Promise<void> =>
    saveDownload(await send(`/documents/${documentId}/pdf`, { credentials: 'same-origin' }), fallbackName),
  downloadMyDocumentFile: async (documentId: string, fallbackName: string): Promise<void> =>
    saveDownload(await send(`/me/documents/${documentId}/file`, { credentials: 'same-origin' }), fallbackName),

  downloadPit11Xml: async (documentId: string): Promise<void> => {
    const response = await send(`/documents/${documentId}/xml`, {
      credentials: 'same-origin',
    })
    if (!response.ok) {
      throw apiErrorFrom(response.status, await response.json().catch(() => null), 'UNKNOWN', 'Nie udało się pobrać pliku')
    }
    const disposition = response.headers.get('content-disposition') ?? ''
    const named = filenameFrom(disposition)
    const url = URL.createObjectURL(await response.blob())
    try {
      const link = document.createElement('a')
      link.href = url
      link.download = named ?? 'PIT-11.xml'
      link.click()
    } finally {
      URL.revokeObjectURL(url)
    }
  },

  /**
   * Plik XML deklaracji rocznej płatnika: PIT-4R albo PIT-8AR (Etap 28).
   *
   * Jak przy PIT-11 idziemy `fetch`-em, nie nawigacją: brak kodu urzędu albo
   * NIP-u kończy się odpowiedzią 409 z listą braków, a tę listę chcemy pokazać
   * zdaniami w ekranie.
   */
  /*
   * Pliki KEDU przez fetch, nie przez nawigację: odmowa (409) ma kod błędu
   * i trafia do ekranu, a nie jako surowy JSON w karcie (przegląd Fali 2).
   */
  downloadKedu: (month: string) => downloadAttachment(`/api/v1/declarations/${month}/kedu`, 'kedu.xml'),
  downloadRegistrationsKedu: () =>
    downloadAttachment('/api/v1/zus-registrations/kedu', 'kedu-zgloszenia.xml'),
  downloadDeclarationXml: async (declarationId: string): Promise<void> => {
    const response = await send(`/declarations/${declarationId}/xml`, {
      credentials: 'same-origin',
    })
    if (!response.ok) {
      throw apiErrorFrom(response.status, await response.json().catch(() => null), 'UNKNOWN', 'Nie udało się pobrać pliku')
    }
    const disposition = response.headers.get('content-disposition') ?? ''
    const named = filenameFrom(disposition)
    const url = URL.createObjectURL(await response.blob())
    try {
      const link = document.createElement('a')
      link.href = url
      link.download = named ?? 'deklaracja.xml'
      link.click()
    } finally {
      URL.revokeObjectURL(url)
    }
  },

  /**
   * Rozkład czasu pracy (Etap 41): dokument z art. 129 § 3 k.p. razem
   * z wymiarem okresu i zastrzeżeniami odpoczynkowymi.
   */
  schedule: (employeeId: string, month: string) =>
    apiRequest<ScheduleResponse>(`/employees/${employeeId}/schedule?month=${month}`),
  generateSchedule: (
    employeeId: string,
    body: { month: string; startsAt: string; endsAt: string },
  ) =>
    apiRequest<ScheduleResponse>(`/employees/${employeeId}/schedule/generate`, {
      method: 'POST',
      body,
    }),
  saveSchedule: (
    employeeId: string,
    body: { month: string; days: readonly ScheduleDayInput[] },
  ) => apiRequest<ScheduleResponse>(`/employees/${employeeId}/schedule`, { method: 'PUT', body }),
  publishSchedule: (employeeId: string, month: string, publishedOn?: string) =>
    apiRequest<ScheduleResponse>(`/employees/${employeeId}/schedule/publish`, {
      method: 'POST',
      body: { month, ...(publishedOn === undefined ? {} : { publishedOn }) },
    }),
  /**
   * Wykonanie kontra rozkład (Etap 42): nadgodziny z art. 151 § 1, dodatek
   * 100 % z art. 151(1) § 1 i pora nocna z okna firmy — te same liczby,
   * którymi liczy naliczenie płac.
   */
  timeReview: (employeeId: string, month: string) =>
    apiRequest<TimeReviewResponse>(`/employees/${employeeId}/time-review?month=${month}`),
  /**
   * Rejestr umów o dzieło i zgłoszeń ZUS RUD (Etap 47). Termin z art. 36
   * ust. 17 u.s.u.s. to 7 dni od dnia ZAWARCIA umowy.
   */
  dzieloContracts: (year: number) =>
    apiRequest<DzieloRegisterResponse>(`/dzielo-contracts?year=${String(year)}`),
  recordDzieloContract: (body: DzieloContractInput) =>
    apiRequest<DzieloRegisterResponse>('/dzielo-contracts', { method: 'POST', body }),
  reportDzieloContract: (contractId: string, reportedOn?: string) =>
    apiRequest<DzieloRegisterResponse>(`/dzielo-contracts/${contractId}/report`, {
      method: 'POST',
      body: reportedOn === undefined ? {} : { reportedOn },
    }),
  /**
   * Rejestr wniosków o IFT-1 (Etap 52). Wniosek jest pisemny, więc portal go
   * nie przyjmuje — kadry zapisują ten, który wpłynął (art. 42 ust. 4 PIT).
   */
  iftRequests: (employeeId?: string, year?: number) => {
    const query = new URLSearchParams()
    if (employeeId !== undefined) query.set('employeeId', employeeId)
    if (year !== undefined) query.set('year', String(year))
    const suffix = query.size === 0 ? '' : `?${query.toString()}`
    return apiRequest<{ items: IftRequestItem[] }>(`/payroll/ift-requests${suffix}`)
  },
  recordIftRequest: (body: IftRequestInput) =>
    apiRequest<{ id: string; deadline: string }>('/payroll/ift-requests', {
      method: 'POST',
      body,
    }),
  issueIftRequest: (requestId: string) =>
    apiRequest<{ documentId: string; file: boolean; deadline: string; late: boolean }>(
      `/payroll/ift-requests/${requestId}/issue`,
      { method: 'POST', body: {} },
    ),

  issueDzieloForm: (employeeId: string) =>
    apiRequest<{ documentId: string; title: string; contracts: number; remaining: number }>(
      '/dzielo-contracts/rud',
      { method: 'POST', body: { employeeId } },
    ),
  /**
   * Granice pracy nadliczbowej na rok (Etap 45): limit z art. 151 § 3, godziny
   * akcji ratowniczej poza limitem i status pracownika zarządzającego.
   */
  saveOvertimeLimits: (employeeId: string, body: OvertimeLimitsInput) =>
    apiRequest<{ limits: OvertimeLimitsView | null }>(
      `/employees/${employeeId}/overtime-limits`,
      { method: 'PUT', body },
    ),
  /**
   * System czasu pracy pracownika i okres rozliczeniowy (Etap 44). Od nich
   * zależy dobowy wymiar w rozkładzie i moment rozliczenia przeciętnej normy.
   */
  workTime: (employeeId: string, month: string) =>
    apiRequest<WorkTimeResponse>(`/employees/${employeeId}/work-time?month=${month}`),
  saveWorkTime: (employeeId: string, body: WorkTimeInput) =>
    apiRequest<WorkTimeResponse>(`/employees/${employeeId}/work-time`, {
      method: 'PUT',
      body,
    }),
  /**
   * Rejestr czasu wolnego w zamian za nadgodziny (art. 151(2) k.p., Etap 43).
   * Wpis jest faktem udzielenia — dodatek za pokryte godziny nie przysługuje.
   */
  overtimeTimeOff: (employeeId: string, month: string) =>
    apiRequest<OvertimeTimeOffResponse>(
      `/employees/${employeeId}/overtime-time-off?month=${month}`,
    ),
  grantOvertimeTimeOff: (
    employeeId: string,
    body: {
      month: string
      mode: 'wniosek' | 'pracodawca'
      grantedFrom: string
      grantedTo: string
      grantedHours: number
      note?: string
    },
  ) =>
    apiRequest<OvertimeTimeOffResponse>(`/employees/${employeeId}/overtime-time-off`, {
      method: 'POST',
      body,
    }),
  revokeOvertimeTimeOff: (employeeId: string, entryId: string) =>
    apiRequest<OvertimeTimeOffResponse>(
      `/employees/${employeeId}/overtime-time-off/${entryId}`,
      { method: 'DELETE' },
    ),
  /**
   * Rejestr dyżurów (art. 151(5) k.p., Etap 50). Wpisem jest fakt: kiedy, jak
   * długo, gdzie i ile z tego czasu pracownik pracował — rekompensatę liczy
   * przepis.
   */
  dutyShifts: (employeeId: string, month: string) =>
    apiRequest<DutyShiftsResponse>(`/employees/${employeeId}/duty-shifts?month=${month}`),
  recordDutyShift: (
    employeeId: string,
    body: {
      month: string
      date: string
      place: 'zaklad' | 'dom'
      hours: number
      workedHours?: number
      timeOffHours?: number
      note?: string
    },
  ) =>
    apiRequest<DutyShiftsResponse>(`/employees/${employeeId}/duty-shifts`, {
      method: 'POST',
      body,
    }),
  grantDutyTimeOff: (employeeId: string, entryId: string, timeOffHours: number) =>
    apiRequest<DutyShiftsResponse>(`/employees/${employeeId}/duty-shifts/${entryId}`, {
      method: 'PUT',
      body: { timeOffHours },
    }),
  revokeDutyShift: (employeeId: string, entryId: string) =>
    apiRequest<DutyShiftsResponse>(`/employees/${employeeId}/duty-shifts/${entryId}`, {
      method: 'DELETE',
    }),
  /**
   * Rejestr dni wolnych w zamian za pracę w niedzielę, święto i w dniu wolnym
   * z rozkładu (art. 151(11) i art. 151(3) k.p., Etap 49). Powód i termin
   * biorą się z faktów — formularz podaje tylko dzień pracy i dzień wolny.
   */
  substituteDaysOff: (employeeId: string, month: string) =>
    apiRequest<SubstituteDaysOffResponse>(
      `/employees/${employeeId}/substitute-days-off?month=${month}`,
    ),
  grantSubstituteDayOff: (
    employeeId: string,
    body: { month: string; workDate: string; grantedOn: string; note?: string },
  ) =>
    apiRequest<SubstituteDaysOffResponse>(`/employees/${employeeId}/substitute-days-off`, {
      method: 'POST',
      body,
    }),
  revokeSubstituteDayOff: (employeeId: string, entryId: string) =>
    apiRequest<SubstituteDaysOffResponse>(
      `/employees/${employeeId}/substitute-days-off/${entryId}`,
      { method: 'DELETE' },
    ),
  /** Portal pracownika: mój rozkład — wyłącznie przekazany (art. 129 § 3). */
  mySchedule: (month: string) =>
    apiRequest<{
      hasEmployeeRecord: boolean
      /** `false` przy umowie cywilnej — rozkład jej nie dotyczy (fala 7b, #35). */
      applies?: boolean
      published: boolean
      publishedOn?: string
      month: string
      normHours?: number
      employeeNormHours?: number
      fteBp?: number
      plannedHours: number
      days: Array<ScheduleDayInput & { holiday?: boolean }>
    }>(`/me/schedule?month=${month}`),

  /**
   * Wykaz akt osobowych — części A–E z § 3 rozporządzenia o dokumentacji
   * pracowniczej, z numeracją wyprowadzoną z chronologii (Etap 40).
   */
  personnelFile: (employeeId: string) =>
    apiRequest<{
      parts: Array<{
        part: string
        entries: Array<{
          documentId: string
          no: number
          label: string
          type: string
          title: string
          date: string | null
          status: string
        }>
        subparts: Array<{
          label: string
          caseId: string | null
          entries: Array<{ documentId: string; label: string; title: string; type: string }>
        }>
      }>
      outside: Array<{
        documentId: string
        label: string
        type: string
        title: string
        date: string | null
        status: string
      }>
    }>(`/employees/${employeeId}/personnel-file`),
  /** Przeniesienie dokumentu między częściami akt; `null` = poza aktami. */
  setDocumentFilePart: (documentId: string, part: string | null) =>
    apiRequest<{ part: string | null }>(`/documents/${documentId}/file-part`, {
      method: 'POST',
      body: { part },
    }),

  /* Kary porządkowe — art. 108–113 k.p. (Etap 38). */
  penalties: (employeeId: string) =>
    apiRequest<{ items: PenaltyItem[] }>(`/employees/${employeeId}/penalties`),
  addPenalty: (employeeId: string, body: PenaltyInput) =>
    apiRequest<{
      id: string
      documentId: string
      garnishmentId: string | null
      voidOn: string
      dailyWageGr: number | null
    }>(`/employees/${employeeId}/penalties`, { method: 'POST', body }),
  penaltyNotified: (id: string, notifiedOn: string) =>
    apiRequest<{ objectionDeadline: string }>(`/penalties/${id}/notified`, {
      method: 'POST',
      body: { notifiedOn },
    }),
  penaltyObjectionDecision: (id: string, accepted: boolean, comment?: string) =>
    apiRequest<{ status: string; notified: boolean }>(`/penalties/${id}/objection/decision`, {
      method: 'POST',
      body: { accepted, ...(comment === undefined ? {} : { comment }) },
    }),
  penaltyVoid: (id: string, body: { basis: PenaltyVoidBasis; reason?: string }) =>
    apiRequest<{ status: string; voidOn: string }>(`/penalties/${id}/void`, {
      method: 'POST',
      body,
    }),
  /** Portal pracownika: moje kary i sprzeciw z art. 112 § 1 k.p. */
  myPenalties: () =>
    apiRequest<{ items: PenaltyItem[]; hasEmployeeRecord: boolean }>('/me/penalties'),
  objectToPenalty: (id: string, comment?: string) =>
    apiRequest<{ objectionSilenceOn: string }>(`/me/penalties/${id}/objection`, {
      method: 'POST',
      body: comment === undefined ? {} : { comment },
    }),

  /* Tytuły do potrącenia z wynagrodzenia — art. 87 k.p. (Etap 33). */
  garnishments: (employeeId: string) =>
    apiRequest<{ items: GarnishmentItem[] }>(`/employees/${employeeId}/garnishments`),
  addGarnishment: (employeeId: string, body: GarnishmentInput) =>
    apiRequest<{ id: string }>(`/employees/${employeeId}/garnishments`, {
      method: 'POST',
      body,
    }),
  /**
   * Oświadczenie pracodawcy po zajęciu wynagrodzenia — art. 882 § 1 k.p.c.
   * (Etap 35). Dokument ląduje w aktach w stanie „do podpisu"; odpowiedź niesie
   * termin, żeby ekran mógł go powtórzyć bez ponownego liczenia.
   */
  garnishmentStatement: (
    id: string,
    body: { servedOn?: string; obstaclesNote?: string },
  ) =>
    apiRequest<{ id: string; title: string; deadline: string }>(
      `/garnishments/${id}/statement`,
      { method: 'POST', body },
    ),
  /* Odbiorca przelewu potrącenia — art. 881 § 3 k.p.c. (Etap 34). */
  setGarnishmentPayee: (id: string, body: { payeeName?: string; bailiffAccount: string }) =>
    apiRequest<{ updated: true }>(`/garnishments/${id}/payee`, { method: 'POST', body }),
  endGarnishment: (id: string, effectiveTo: string) =>
    apiRequest<{ ended: true }>(`/garnishments/${id}/end`, {
      method: 'POST',
      body: { effectiveTo },
    }),

  /* Członkowie rodziny w ubezpieczeniu zdrowotnym — formularz ZUS ZCNA (Etap 30). */
  familyMembers: (employeeId: string) =>
    apiRequest<FamilyMembersResponse>(`/employees/${employeeId}/family`),
  addFamilyMember: (employeeId: string, body: FamilyMemberInput) =>
    apiRequest<{ id: string }>(`/employees/${employeeId}/family`, { method: 'POST', body }),
  endFamilyMember: (id: string, body: { entitlementTo: string; declaredAt: string }) =>
    apiRequest<{ ended: true }>(`/family/${id}/end`, { method: 'POST', body }),
  myFamilyMembers: () => apiRequest<FamilyMembersResponse>('/me/family'),

  /* Zgłoszenia ubezpieczeniowe ZUS ZUA i ZWUA (Etap 16). */
  zusRegistrations: () => apiRequest<ZusRegistrationsResponse>('/zus-registrations'),
  generateZusRegistrations: () =>
    apiRequest<{ generated: number; blocked: number; removed?: number }>(
      '/zus-registrations/generate',
      { method: 'POST' },
    ),
  /* `skipped`: dokumenty, których nie ma w pliku KEDU, więc nie poszły do ZUS-u (#8). */
  markZusRegistrationsSent: (ids: string[]) =>
    apiRequest<{ marked: number; skipped?: number }>('/zus-registrations/sent', {
      method: 'POST',
      body: { ids },
    }),
  /**
   * Korekta przekazanego zgłoszenia (Etap 31). Odpowiedź mówi, KTÓRA droga
   * została wybrana: jeden dokument w trybie korekty albo para
   * wyrejestrowanie + zgłoszenie od nowa.
   */
  correctZusRegistration: (id: string, scope: RegistrationCorrectionScope) =>
    apiRequest<{
      path: 'korekta' | 'wyrejestrowanie_i_zgloszenie'
      created: Array<{ id: string; kind: string }>
    }>(`/zus-registrations/${id}/correct`, { method: 'POST', body: { scope } }),
  zusRegistrationsKeduUrl: () => '/api/v1/zus-registrations/kedu',
  /* Pliki pobierane nawigacją — przez `openFile`, żeby obraz demo mógł odmówić. */
  myPayslipPdfUrl: (payslipId: string) => `/api/v1/me/pay/${payslipId}/pdf`,
  attachmentUrl: (fileId: string) => `/api/v1/files/${fileId}`,

  /* Deklaracje ZUS/PIT/PPK i plik KEDU (Etap 10). */
  declarations: (month?: string) =>
    apiRequest<DeclarationsResponse>(
      `/declarations${month === undefined ? '' : `?month=${month}`}`,
    ),
  generateDeclarations: (month: string) =>
    apiRequest<{
      month: string
      generated: DeclarationKind[]
      blocked: DeclarationKind[]
      /** Dokumenty, których komplet korygujący nie zawiera (raport bez zmian). */
      skipped: DeclarationKind[]
      /** Numer identyfikatora kompletu: 1 = pierwotny, 2+ = korygujący. */
      setNumber: number
      correction: boolean
    }>(`/declarations/${month}/generate`, { method: 'POST' }),
  markDeclarationSent: (id: string) =>
    apiRequest<{ status: 'sent' }>(`/declarations/${id}/sent`, { method: 'POST' }),
  /** Pobranie pliku idzie przez nawigację przeglądarki — serwer oddaje załącznik. */
  declarationFileUrl: (id: string) => `/api/v1/declarations/${id}/file`,
  /* PIT-4R: deklaracja ROCZNA płatnika, osobno od kompletu miesiąca (Etap 22). */
  pit4r: (year: number) => apiRequest<Pit4rView>(`/declarations/pit4r?year=${year}`),
  pit8ar: (year: number) => apiRequest<Pit8arView>(`/declarations/pit8ar?year=${year}`),
  issuePit8ar: (year: number, acknowledgeOpenYear = false) =>
    apiRequest<{ id: string; setNumber: number; totalGr: number; deadline: string }>(
      '/declarations/pit8ar',
      { method: 'POST', body: { year, ...(acknowledgeOpenYear ? { acknowledgeOpenYear } : {}) } },
    ),
  issuePit4r: (year: number, acknowledgeOpenYear = false) =>
    apiRequest<{ id: string; setNumber: number; totalGr: number; deadline: string }>(
      '/declarations/pit4r',
      { method: 'POST', body: { year, ...(acknowledgeOpenYear ? { acknowledgeOpenYear } : {}) } },
    ),
  keduUrl: (month: string) => `/api/v1/declarations/${month}/kedu`,

  /* Dziennik audytu i asystent (Etap 7). */
  audit: (query: Record<string, string> = {}) =>
    apiRequest<AuditResponse>(`/audit?${new URLSearchParams(query).toString()}`),
  auditProof: (id: number) => apiRequest<AuditProof>(`/audit/${id}/proof`),
  assistantHints: (view: AssistantView) =>
    apiRequest<{ view: AssistantView; hints: AssistantHint[] }>(
      `/assistant/hints?view=${encodeURIComponent(view)}`,
    ),

  /* Wnioski o zmianę danych — jedyna droga zapisu do akt (§17 pkt 2). */
  changeRequests: (query = '') =>
    apiRequest<{ items: ChangeRequestItem[] }>(`/change-requests${query ? `?${query}` : ''}`),
  createChangeRequest: (body: {
    employeeId: string
    fieldKey: ChangeRequestField
    value: Record<string, string>
    comment?: string
    attachmentFileId?: string
  }) => apiRequest<{ id: string }>('/change-requests', { method: 'POST', body }),
  approveChangeRequest: (id: string) =>
    apiRequest<{ status: RequestStatus }>(`/change-requests/${id}/approve`, { method: 'POST' }),
  rejectChangeRequest: (id: string, comment?: string) =>
    apiRequest<{ status: RequestStatus }>(`/change-requests/${id}/reject`, {
      method: 'POST',
      body: { comment },
    }),
  /** Wysyłka pliku idzie multipartem, więc omija `apiRequest` (JSON-only). */
  uploadFile: async (file: File): Promise<UploadedFile> => {
    const form = new FormData()
    form.append('file', file)
    const csrf = readCookie(CSRF_COOKIE)
    const response = await send('/files', {
      method: 'POST',
      credentials: 'same-origin',
      ...(csrf ? { headers: { [CSRF_HEADER]: csrf } } : {}),
      body: form,
    })
    if (!response.ok) {
      throw apiErrorFrom(response.status, await response.json().catch(() => null), 'UPLOAD_FAILED', 'Nie udało się wysłać pliku')
    }
    return (await response.json()) as UploadedFile
  },

  /** Pola akt pracownika wraz z obecnymi wartościami (portal nie zna swojego id). */
  myChangeFields: () =>
    apiRequest<{ employeeId: string | null; values: Partial<Record<ChangeRequestField, string | null>> }>(
      '/me/change-fields',
    ),

  /* Wnioski portalowe. */
  requestTypes: () => apiRequest<{ items: PortalRequestTypeItem[] }>('/me/request-types'),
  myRequests: () =>
    apiRequest<{
      items: PortalRequestItem[]
      /* Historia z trzech źródeł (fala 8, grupy 38 i 40); stare odpowiedzi ich nie mają. */
      changes?: PortalChangeRequestItem[]
      leaves?: PortalLeaveHistoryItem[]
      hasEmployeeRecord: boolean
    }>('/me/requests'),
  /** Konfiguracja kafelków portalu pod „Słownikami" (grupa 37). */
  settingsRequestTypes: () => apiRequest<{ items: RequestTypeSetting[] }>('/settings/request-types'),
  saveSettingsRequestTypes: (items: Array<{ type: PortalRequestType; enabled: boolean }>) =>
    apiRequest<{ enabled: number }>('/settings/request-types', { method: 'PUT', body: { items } }),
  submitRequest: (body: {
    type: PortalRequestType
    dateFrom?: string
    dateTo?: string
    note?: string
    /** Wniosek o członka rodziny — dane z ZUS ZCNA (Etap 37). */
    family?:
      | {
          action: 'zgloszenie'
          member: Omit<FamilyMemberInput, 'declaredAt'>
        }
      | { action: 'wyrejestrowanie'; memberId: string; entitlementTo: string }
  }) => apiRequest<{ id: string; status: RequestStatus }>('/me/requests', { method: 'POST', body }),
  hrRequests: (status: 'all' | RequestStatus = 'pending') =>
    apiRequest<HrRequestsResponse>(`/requests?status=${status}`),
  /**
   * `acknowledgeWorkEntries` — zwolnienie z wniosku na dni z wpisami pracy,
   * świadomie i z audytem (fala 7b, grupa 133).
   */
  decideRequest: (
    id: string,
    decision: 'approve' | 'reject',
    comment?: string,
    acknowledgeWorkEntries?: boolean,
  ) =>
    apiRequest<{ status: RequestStatus }>(`/requests/${id}/${decision}`, {
      method: 'POST',
      body: { comment, ...(acknowledgeWorkEntries ? { acknowledgeWorkEntries: true } : {}) },
    }),

  /* Zaproszenia i proces zatrudnienia po stronie działu HR. */
  createInvitation: (body: InviteBody) =>
    apiRequest<CreatedInvitation>('/invitations', { method: 'POST', body }),
  sendInvitationEmail: (id: string, code: string) =>
    apiRequest<{ sent: true }>(`/invitations/${id}/send-email`, { method: 'POST', body: { code } }),
  revokeInvitation: (id: string) =>
    apiRequest<void>(`/invitations/${id}`, { method: 'DELETE' }),
  invitations: () => apiRequest<{ items: InvitationListItem[] }>('/invitations'),
  /* Import zaproszeń z pliku (Etap 54, ADR-0060): podgląd bez zapisu, potem zapis. */
  previewInvitationImport: (csv: string) =>
    apiRequest<InvitationImportPreview>('/invitations/import/preview', { method: 'POST', body: { csv } }),
  importInvitations: (csv: string) =>
    apiRequest<InvitationImportResult>('/invitations/import', { method: 'POST', body: { csv } }),
  /* Przeniesienie kadry z innego programu (Etap 55, ADR-0061). */
  migrationState: () => apiRequest<MigrationState>('/migration'),
  previewMigration: (body: MigrationBody) =>
    apiRequest<MigrationAnalysis>('/migration/preview', { method: 'POST', body }),
  commitMigration: (body: MigrationBody) =>
    apiRequest<MigrationResult>('/migration', { method: 'POST', body }),
  revertMigration: () => apiRequest<{ people: number }>('/migration/revert', { method: 'POST', body: {} }),
  employeeOnboarding: (id: string) =>
    apiRequest<EmployeeOnboardingResponse>(`/employees/${id}/onboarding`),
  approveEmployeeData: (id: string) =>
    apiRequest<{ stage: OnboardingStage }>(`/employees/${id}/onboarding/approve-data`, {
      method: 'POST',
    }),
  /* `notifiedEmployee` — czy wiadomość do pracownika naprawdę wyszła (Fala 6, grupy 90 i 138). */
  returnEmployeeData: (id: string, comment: string) =>
    apiRequest<{ stage: OnboardingStage; notifiedEmployee: boolean }>(
      `/employees/${id}/onboarding/return-data`,
      { method: 'POST', body: { comment } },
    ),
  prepareTerms: (id: string, body: PrepareTermsBody) =>
    apiRequest<{ stage: OnboardingStage; notifiedEmployee: boolean }>(
      `/employees/${id}/onboarding/prepare-terms`,
      { method: 'POST', body },
    ),
  issueReferral: (id: string, deadlineDays: number) =>
    apiRequest<{ stage: OnboardingStage; notifiedEmployee: boolean }>(
      `/employees/${id}/onboarding/issue-referral`,
      { method: 'POST', body: { deadlineDays } },
    ),
  approveMedical: (id: string) =>
    apiRequest<{ stage: OnboardingStage }>(`/employees/${id}/onboarding/approve-medical`, {
      method: 'POST',
    }),
  confirmBhp: (id: string, body: { performedAt: string; conductedBy: string; validUntil?: string }) =>
    apiRequest<{ stage: OnboardingStage; accountCreated: boolean; accountMailed?: boolean }>(
      `/employees/${id}/onboarding/confirm-bhp`,
      { method: 'POST', body },
    ),
  answerOnboardingQuestion: (id: string, body: string) =>
    apiRequest<{ sideState: OnboardingSideState; notifiedEmployee: boolean }>(
      `/employees/${id}/onboarding/messages`,
      { method: 'POST', body: { body } },
    ),

  /* Portal onboardingu — działa na własnej sesji (cookie k1p4_onboarding). */
  invitationPreview: (code: string) =>
    apiRequest<InvitationPreview>(`/onboarding/invitation?code=${encodeURIComponent(code)}`),
  onboardingJoin: (code: string, email: string) =>
    apiRequest<{ ok: true }>('/onboarding/join', { method: 'POST', body: { code, email } }),
  onboardingSession: (resumeToken?: string) =>
    apiRequest<OnboardingSessionResponse>(
      `/onboarding/session${resumeToken ? `?s=${encodeURIComponent(resumeToken)}` : ''}`,
    ),
  onboardingSavePersonal: (body: Record<string, string | undefined>) =>
    apiRequest<{ saved: true }>('/onboarding/personal-data', { method: 'PUT', body }),
  onboardingSaveTaxBank: (body: Record<string, string | undefined>) =>
    apiRequest<{ saved: true }>('/onboarding/tax-bank', { method: 'PUT', body }),
  onboardingSaveEmergency: (body: Record<string, string | undefined>) =>
    apiRequest<{ saved: true }>('/onboarding/emergency', { method: 'PUT', body }),
  onboardingSubmit: () =>
    apiRequest<{ stage: OnboardingStage }>('/onboarding/submit', {
      method: 'POST',
      body: { rodoConsent: true },
    }),
  /** Akceptuje ofertę, którą kandydat ma na ekranie — zmieniona przez kadry daje 409 TERMS_CHANGED. */
  onboardingAcceptTerms: (termsId: string, rulesAcknowledged: boolean, rodoAcknowledged = false) =>
    apiRequest<{ stage: OnboardingStage }>('/onboarding/terms/accept', {
      method: 'POST',
      body: { infoAcknowledged: true, rulesAcknowledged, termsId, ...(rodoAcknowledged ? { rodoAcknowledged } : {}) },
    }),
  onboardingRejectTerms: () =>
    apiRequest<{ sideState: OnboardingSideState }>('/onboarding/terms/reject', { method: 'POST' }),
  onboardingAskQuestion: (body: string) =>
    apiRequest<{ sideState: OnboardingSideState }>('/onboarding/terms/question', {
      method: 'POST',
      body: { body },
    }),
  onboardingConfirmMedical: (examDate: string, validUntil: string) =>
    apiRequest<{ stage: OnboardingStage; warnings: string[] }>('/onboarding/medical/confirm', {
      method: 'POST',
      body: { examDate, validUntil, confirmed: true },
    }),
  onboardingDocument: (key: OnboardingDocumentKey) =>
    apiRequest<OnboardingDocument>(`/onboarding/documents/${key}`),
  /** Struktura organizacyjna — działy z liczbą przypisanych osób. */
  departments: () => apiRequest<{ items: DepartmentRow[] }>('/departments'),
  createDepartment: (name: string) =>
    apiRequest<DepartmentRow>('/departments', { method: 'POST', body: { name } }),
  renameDepartment: (id: string, name: string) =>
    apiRequest<DepartmentRow>(`/departments/${id}`, { method: 'PUT', body: { name } }),
  deleteDepartment: (id: string) =>
    apiRequest<void>(`/departments/${id}`, { method: 'DELETE' }),
  tenantMe: () => apiRequest<TenantMeResponse>('/tenants/me'),
  joinRequests: () => apiRequest<{ items: JoinRequestItem[] }>('/tenants/join-requests'),
  approveJoinRequest: (id: string, role: string) =>
    apiRequest<{ ok: true }>(`/tenants/join-requests/${id}/approve`, {
      method: 'POST',
      body: { role },
    }),
  rejectJoinRequest: (id: string) =>
    apiRequest<{ ok: true }>(`/tenants/join-requests/${id}/reject`, { method: 'POST' }),
  users: () => apiRequest<{ items: TenantUserItem[] }>('/users'),
  setUserRoles: (id: string, roles: string[]) =>
    apiRequest<{ ok: true }>(`/users/${id}/roles`, { method: 'PUT', body: { roles } }),
}
